// src/pages/AdminNornsWeave.js - Enhanced Admin Dashboard
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Logo } from '../components/Logo';
import { db } from '../firebase';
import { collection, getDocs, deleteDoc, doc, updateDoc, setDoc, addDoc } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import {
  sendBookingApprovedEmail,
  sendBookingDeclinedEmail,
  sendBookingEmail,
  sendBookingCancellationEmail,
  sendAideClosureEmail,
  sendLibraryAssistantClosureEmail,
  sendLibraryChairsClosureEmail,
  sendRecessCancellationEmail,
  sendAdminClosureCCEmail,
} from '../utils/emailService';
import { formatTime12 } from '../components/TimePicker';
import { shiftDateToNewYear, shiftDateRangeToNewYear, isDateOnDayOfWeek } from '../utils/dateShiftUtils';

// Import extracted tab components and context
import {
  AdminNornsWeaveProvider,
  useAdminNornsWeave,
  CLOSURE_REASONS,
  ListTab,
  StatsTab,
  PrintTab,
  PendingTab,
  AidesTab,
  FixedClassesTab,
  CalendarTab,
  YearSetupTab,
  SettingsTab,
  // Modals
  DeclineModal,
  AddBlockModal,
  ClosureModal,
  AddEventModal,
  AddFixedClassModal,
  EditFixedClassModal,
  AddAideModal,
  EditAideModal,
  EditBookingModal,
  AddCalendarEventModal,
  AddOverrideModal,
  AddSingleClassModal,
  YearSetupWizard
} from '../components/admin/nornsweave';

function AdminNornsWeave() {
  const navigate = useNavigate();
  const { logout } = useAuth();

  // Get shared state from context
  const {
    currentUser,
    bookings,
    pendingBookings,
    personalEvents,
    fixedClasses,
    aides,
    teachers,
    settings, setSettings,
    closures,
    holyDays,
    adorationDays,
    schoolCalendarEvents,
    massTimeBlocks,
    saturdayOpenings,
    isYearSetupLocked, setIsYearSetupLocked,
    yearSetupForm, setYearSetupForm,
    saturdaySchedule, setSaturdaySchedule,
    settingsForm, setSettingsForm,
    view, setView,
    weekOffset,
    loading,
    notification, showNotification,
    loadSettings, loadBookings, loadPendingBookings, loadPersonalEvents,
    loadFixedClasses, loadAides, loadTeachers, loadClosures, loadYearSetupData,
    getWeekDates, getDayName, getDayOfWeekFromDate, getLibraryHours, timeToMinutes,
    isSaturdayOpen, getSaturdayHours,
    weekDates, thisWeekBookings,
    // Email notification helpers
    getTeachersForRecess,
    getAffectedRecesses,
    getAidesForDayType,
    getLibraryChairs,
    getLibraryAssistant,
    isValidEmail
  } = useAdminNornsWeave();

  // Local UI state (modals, forms) - these don't need to be in context
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [showAddBlockModal, setShowAddBlockModal] = useState(false);
  const [showAddFixedClassModal, setShowAddFixedClassModal] = useState(false);
  const [showEditFixedClassModal, setShowEditFixedClassModal] = useState(false);
  const [editingFixedClass, setEditingFixedClass] = useState(null);
  const [showAddAideModal, setShowAddAideModal] = useState(false);
  const [showEditAideModal, setShowEditAideModal] = useState(false);
  const [editingAide, setEditingAide] = useState(null);
  const [showAddOverrideModal, setShowAddOverrideModal] = useState(false);
  const [newOverride, setNewOverride] = useState({ startDate: '', endDate: '', type: 'no_classes', description: '', blockedHours: [], library: 'both' });
  const [showAddSingleClassModal, setShowAddSingleClassModal] = useState(false);
  const [newSingleClass, setNewSingleClass] = useState({
    teacherName: '',
    teacherEmail: '',
    grade: '',
    subject: '',
    date: '',
    startTime: '',
    duration: '',
    endTime: '',
    library: 'elementary',
    studentCount: '',
    bookingType: 'class'
  });
  const [showAddCalendarEventModal, setShowAddCalendarEventModal] = useState(false);
  const [newCalendarEvent, setNewCalendarEvent] = useState({ date: '', type: 'closed', description: '' });
  const [showClosureModal, setShowClosureModal] = useState(false);
  const [closureForm, setClosureForm] = useState({
    reason: '',
    customReason: '',
    dateType: 'single',
    startDate: '',
    endDate: '',
    timeType: 'full',
    startTime: '',
    endTime: '',
    library: 'both',
    notifyOption: 'immediate',
    customMessage: '',
    // New notification fields
    dayType: 'na',
    notifyRecessTeachers: true,
    notifyAides: true,
    aideWorkAssignment: '',
    aideReportTo: '',
    notifyLibraryAssistant: true,
    notifyLibraryChairs: true,
    adminContactIds: [],
    additionalCcEmail: ''
  });
  const [affectedBookings, setAffectedBookings] = useState([]);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineBooking, setDeclineBooking] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [declineAlternatives, setDeclineAlternatives] = useState('');

  // Year Setup Wizard State (local to this component)
  const [showYearSetupWizard, setShowYearSetupWizard] = useState(false);
  const [yearSetupStep, setYearSetupStep] = useState(1);
  const [wizardHolyDays, setWizardHolyDays] = useState([]);
  const [wizardAdorationDays, setWizardAdorationDays] = useState([]);
  const [wizardHolidays, setWizardHolidays] = useState([]);
  const [wizardHalfDays, setWizardHalfDays] = useState([]);
  const [wizardWorkdays, setWizardWorkdays] = useState([]);
  const [wizardLibraryEvents, setWizardLibraryEvents] = useState([]);
  const [wizardSaturdayConfig, setWizardSaturdayConfig] = useState({
    enabled: false,
    startTime: '09:00',
    endTime: '13:00',
    dates: []
  });

  // Copy from previous year state
  const [isCopyingYear, setIsCopyingYear] = useState(false);

  // Saving state to prevent duplicate submissions
  const [savingHolyDay, setSavingHolyDay] = useState(false);
  const [savingAdorationDay, setSavingAdorationDay] = useState(false);
  const [savingSchoolCalendarEvent, setSavingSchoolCalendarEvent] = useState(false);
  const [savingMassBlock, setSavingMassBlock] = useState(false);

  const [newEvent, setNewEvent] = useState({
    date: '',
    endDate: '',
    dateType: 'single',
    startTime: '',
    endTime: '',
    timeType: 'allDay',
    title: '',
    library: 'both',
    type: 'personal'
  });

  const [newBlock, setNewBlock] = useState({
    day: 'Monday',
    startTime: '',
    endTime: '',
    reason: 'Planning Period',
    library: 'both'
  });

  const [newFixedClass, setNewFixedClass] = useState({
    teacherName: '',
    teacherEmail: '',
    grade: '',
    subject: '',
    dayOfWeek: 'Monday',
    startTime: '',
    duration: '',
    endTime: '',
    library: 'elementary',
    studentCount: ''
  });

  const [newAide, setNewAide] = useState({
    name: '',
    email: '',
    dayType: 'A', // 'A' or 'B'
    assignedBlocks: [] // array of block IDs
  });

  // Data loading is handled by context - no duplicate useEffect or load functions needed

  // Helper function to hash passwords via Cloud Function (secure server-side hashing)
  const hashPassword = async (password) => {
    const hashUrl = process.env.NODE_ENV === 'production'
      ? 'https://us-central1-heimdl.cloudfunctions.net/hashNornsWeavePassword'
      : 'http://localhost:5001/heimdl/us-central1/hashNornsWeavePassword';

    const response = await fetch(hashUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });

    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Failed to hash password');
    }
    return result.hashedPassword;
  };

  const handleAddTeacher = async (teacherData) => {
    try {
      await addDoc(collection(db, 'users', currentUser.uid, 'nornsWeaveTeachers'), teacherData);
      await loadTeachers();
      showNotification('Teacher added successfully', 'success');
    } catch (err) {
      console.error('Error adding teacher:', err);
      showNotification('Error adding teacher', 'error');
    }
  };

  const handleDeleteTeacher = async (teacherId) => {
    try {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveTeachers', teacherId));
      await loadTeachers();
      showNotification('Teacher removed', 'success');
    } catch (err) {
      console.error('Error deleting teacher:', err);
      showNotification('Error removing teacher', 'error');
    }
  };

  const handleApproveBooking = async (pendingBooking) => {
    try {
      const { id, ...bookingData } = pendingBooking;
      await addDoc(collection(db, 'users', currentUser.uid, 'nornsWeaveBookings'), {
        ...bookingData,
        status: 'approved',
        approvedAt: new Date().toISOString(),
        approvedBy: currentUser.email
      });
      await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeavePending', id));

      // Send approval email if email is enabled
      if (settings?.emailEnabled && bookingData.teacherEmail) {
        sendBookingApprovedEmail(bookingData, settings).catch(err => {
          console.error('Failed to send approval email:', err);
        });
      }

      await loadBookings();
      await loadPendingBookings();
      alert('Booking approved successfully!');
    } catch (err) {
      console.error('Error approving booking:', err);
      alert('Error approving booking. Please try again.');
    }
  };

  const openDeclineModal = (booking) => {
    setDeclineBooking(booking);
    setDeclineReason('');
    setDeclineAlternatives('');
    setShowDeclineModal(true);
  };

  const handleDeclineBooking = async () => {
    if (!declineBooking) return;
    try {
      // Update the pending booking with rejection info before deleting
      await updateDoc(doc(db, 'users', currentUser.uid, 'nornsWeavePending', declineBooking.id), {
        status: 'rejected',
        rejectionReason: declineReason,
        suggestedAlternatives: declineAlternatives,
        rejectedAt: new Date().toISOString(),
        rejectedBy: currentUser.email
      });

      // Send decline email if email is enabled
      if (settings?.emailEnabled && declineBooking.teacherEmail) {
        sendBookingDeclinedEmail(declineBooking, settings, declineReason, declineAlternatives).catch(err => {
          console.error('Failed to send decline email:', err);
        });
      }

      // Then delete it
      await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeavePending', declineBooking.id));
      await loadPendingBookings();
      setShowDeclineModal(false);
      setDeclineBooking(null);
      alert('Booking declined.');
    } catch (err) {
      console.error('Error declining booking:', err);
      alert('Error declining booking. Please try again.');
    }
  };

  // Auto-save settings (non-credential settings)
  const autoSaveSettings = async (newSettingsForm) => {
    try {
      const settingsData = {
        username: settings?.username || settingsForm.username,
        password: settings?.password || settingsForm.password,
        schoolName: settings?.schoolName || settingsForm.schoolName,
        cityName: settings?.cityName || settingsForm.cityName,
        yourName: settings?.yourName || settingsForm.yourName,
        elementaryHours: {
          start: newSettingsForm.elementaryStart,
          end: newSettingsForm.elementaryEnd
        },
        middleSchoolHours: {
          start: newSettingsForm.middleSchoolStart,
          end: newSettingsForm.middleSchoolEnd
        },
        librarianBlocks: newSettingsForm.librarianBlocks,
        emailEnabled: newSettingsForm.emailEnabled,
        librarianEmail: newSettingsForm.librarianEmail,
        currentDayType: newSettingsForm.currentDayType,
        academicCalendar: newSettingsForm.academicCalendar,
        scheduleOverrides: newSettingsForm.scheduleOverrides,
        aideScheduleBlocks: newSettingsForm.aideScheduleBlocks,
        colors: newSettingsForm.colors
      };

      await setDoc(doc(db, 'users', currentUser.uid, 'settings', 'nornsWeave'), settingsData);
      setSettings(settingsData);
    } catch (err) {
      console.error('Error auto-saving settings:', err);
    }
  };

  // Save credentials only (username/password/school info)
  const handleCredentialsSave = async () => {
    try {
      // Username: first word of school name + city + last name
      const schoolFirstWord = settingsForm.schoolName.split(/\s+/)[0] || '';
      const nameParts = settingsForm.yourName.trim().split(/\s+/);
      const lastName = nameParts[nameParts.length - 1] || '';
      const generatedUsername = `${schoolFirstWord}${settingsForm.cityName}${lastName}`;

      // Hash the password securely via Cloud Function
      const hashedPassword = await hashPassword(settingsForm.password);

      const settingsData = {
        ...settings,
        username: generatedUsername,
        password: settingsForm.password, // Keep plain text in private settings for display
        schoolName: settingsForm.schoolName,
        cityName: settingsForm.cityName,
        yourName: settingsForm.yourName
      };

      await setDoc(doc(db, 'users', currentUser.uid, 'settings', 'nornsWeave'), settingsData);

      // Store hashed password in public auth collection
      await setDoc(doc(db, 'nornsWeavePublic', currentUser.uid), {
        username: generatedUsername,
        password: hashedPassword, // Stored as bcrypt hash
        userId: currentUser.uid,
        schoolName: settingsForm.schoolName,
        librarianName: settingsForm.yourName
      });

      setSettings(settingsData);
      setSettingsForm({...settingsForm, username: generatedUsername});
      showNotification('Credentials saved!', 'success');
    } catch (err) {
      console.error('Error saving credentials:', err);
      showNotification('Error saving credentials: ' + err.message, 'error');
    }
  };

  const handleSettingsSave = async (e) => {
    e.preventDefault();
    try {
      // Username: first word of school name + city + last name
      const schoolFirstWord = settingsForm.schoolName.split(/\s+/)[0] || '';
      const nameParts = settingsForm.yourName.trim().split(/\s+/);
      const lastName = nameParts[nameParts.length - 1] || '';
      const generatedUsername = `${schoolFirstWord}${settingsForm.cityName}${lastName}`;

      // Hash the password securely via Cloud Function
      const hashedPassword = await hashPassword(settingsForm.password);

      const settingsData = {
        username: generatedUsername,
        password: settingsForm.password, // Keep plain text in private settings for display
        schoolName: settingsForm.schoolName,
        cityName: settingsForm.cityName,
        yourName: settingsForm.yourName,
        elementaryHours: {
          start: settingsForm.elementaryStart,
          end: settingsForm.elementaryEnd
        },
        middleSchoolHours: {
          start: settingsForm.middleSchoolStart,
          end: settingsForm.middleSchoolEnd
        },
        librarianBlocks: settingsForm.librarianBlocks,
        emailEnabled: settingsForm.emailEnabled,
        librarianEmail: settingsForm.librarianEmail,
        currentDayType: settingsForm.currentDayType,
        academicCalendar: settingsForm.academicCalendar,
        scheduleOverrides: settingsForm.scheduleOverrides,
        aideScheduleBlocks: settingsForm.aideScheduleBlocks,
        colors: settingsForm.colors
      };

      await setDoc(doc(db, 'users', currentUser.uid, 'settings', 'nornsWeave'), settingsData);

      // Store hashed password in public auth collection
      await setDoc(doc(db, 'nornsWeavePublic', currentUser.uid), {
        username: generatedUsername,
        password: hashedPassword, // Stored as bcrypt hash
        userId: currentUser.uid,
        schoolName: settingsForm.schoolName,
        librarianName: settingsForm.yourName
      });

      setSettings(settingsData);
      setSettingsForm({...settingsForm, username: generatedUsername});
      showNotification('Settings saved successfully!', 'success');
    } catch (err) {
      console.error('Error saving settings:', err);
      showNotification('Error saving settings: ' + err.message, 'error');
    }
  };

  const handleAddLibrarianBlock = () => {
    if (!newBlock.startTime || !newBlock.endTime) {
      alert('Please fill in all required fields');
      return;
    }
    const updatedBlocks = [...settingsForm.librarianBlocks, { ...newBlock, id: Date.now() }];
    const newForm = { ...settingsForm, librarianBlocks: updatedBlocks };
    setSettingsForm(newForm);
    autoSaveSettings(newForm);
    setNewBlock({ day: 'Monday', startTime: '', endTime: '', reason: 'Planning Period', library: 'both' });
    setShowAddBlockModal(false);
    showNotification('Schedule block added', 'success');
  };

  const handleRemoveLibrarianBlock = (blockId) => {
    const updatedBlocks = settingsForm.librarianBlocks.filter(b => b.id !== blockId);
    const newForm = { ...settingsForm, librarianBlocks: updatedBlocks };
    setSettingsForm(newForm);
    autoSaveSettings(newForm);
    showNotification('Schedule block removed', 'success');
  };

  const handleAddPersonalEvent = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'users', currentUser.uid, 'nornsWeaveEvents'), newEvent);
      setNewEvent({ date: '', endDate: '', dateType: 'single', startTime: '', endTime: '', timeType: 'allDay', title: '', library: 'both', type: 'personal' });
      setShowAddEventModal(false);
      await loadPersonalEvents();
    } catch (err) {
      console.error('Error adding event:', err);
      alert('Error adding event.');
    }
  };

  const handleDeletePersonalEvent = async (eventId) => {
    try {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveEvents', eventId));
      await loadPersonalEvents();
      showNotification('Library event deleted', 'success');
    } catch (err) {
      console.error('Error deleting event:', err);
      showNotification('Error deleting event', 'error');
    }
  };

  // Delete a closure
  const handleDeleteClosure = async (closureId) => {
    try {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveClosures', closureId));
      await loadClosures();
      showNotification('Closure deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting closure:', err);
      showNotification('Error deleting closure', 'error');
    }
  };

  // Find bookings affected by a closure
  const findAffectedBookings = (closure) => {
    const { startDate, endDate, timeType, startTime, endTime, library } = closure;
    const closureEndDate = endDate || startDate;

    return bookings.filter(booking => {
      // Check if booking date falls within closure range
      if (booking.date < startDate || booking.date > closureEndDate) return false;

      // Check library match
      if (library !== 'both' && booking.library !== library && booking.library !== 'both') return false;

      // For full day closure, all bookings on that day are affected
      if (timeType === 'full') return true;

      // For partial closure, check time overlap
      if (startTime && endTime) {
        return (booking.startTime < endTime && booking.endTime > startTime);
      }

      return true;
    });
  };

  // Update affected bookings when closure form changes
  const updateAffectedBookings = (newClosureForm) => {
    if (newClosureForm.startDate) {
      const affected = findAffectedBookings(newClosureForm);
      setAffectedBookings(affected);
    } else {
      setAffectedBookings([]);
    }
  };

  // Save closure and notify teachers
  const handleSaveClosure = async () => {
    if (!closureForm.reason || !closureForm.startDate) {
      alert('Please select a reason and date for the closure');
      return;
    }

    const reasonLabel = closureForm.reason === 'other'
      ? closureForm.customReason
      : CLOSURE_REASONS.find(r => r.value === closureForm.reason)?.label || closureForm.reason;

    try {
      // Save closure to Firestore
      const closureData = {
        type: 'library_closure',
        reason: closureForm.reason,
        reasonLabel,
        startDate: closureForm.startDate,
        endDate: closureForm.dateType === 'range' ? closureForm.endDate : closureForm.startDate,
        timeType: closureForm.timeType,
        startTime: closureForm.timeType === 'partial' ? closureForm.startTime : null,
        endTime: closureForm.timeType === 'partial' ? closureForm.endTime : null,
        library: closureForm.library,
        customMessage: closureForm.customMessage,
        notifyOption: closureForm.notifyOption,
        dayType: closureForm.dayType,
        createdAt: new Date().toISOString(),
        affectedBookingIds: affectedBookings.map(b => b.id)
      };

      await addDoc(collection(db, 'users', currentUser.uid, 'nornsWeaveClosures'), closureData);

      // Update any affected bookings to cancelled status
      for (const booking of affectedBookings) {
        await updateDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveBookings', booking.id), {
          status: 'cancelled',
          cancelledReason: `Library closed: ${reasonLabel}`,
          cancelledAt: new Date().toISOString()
        });
      }

      // Handle notifications based on notifyOption
      if (closureForm.notifyOption === 'none') {
        showNotification('Closure saved (no notifications sent)', 'success');
        setShowClosureModal(false);
        await loadBookings();
        await loadClosures();
        return;
      }

      // For scheduled notifications (1 week before), save to scheduledNotifications
      if (closureForm.notifyOption === '1week' || closureForm.notifyOption === '2weeks') {
        const daysBeforeNotice = closureForm.notifyOption === '2weeks' ? 14 : 7;
        const scheduledDate = new Date(closureForm.startDate);
        scheduledDate.setDate(scheduledDate.getDate() - daysBeforeNotice);

        await addDoc(collection(db, 'users', currentUser.uid, 'scheduledNotifications'), {
          type: 'closure',
          closureData: {
            ...closureForm,
            reasonLabel,
          },
          affectedBookingIds: affectedBookings.map(b => b.id),
          scheduledFor: scheduledDate.toISOString().split('T')[0],
          status: 'pending',
          createdAt: new Date().toISOString(),
          settings: {
            yourName: settings?.yourName,
            schoolName: settings?.schoolName,
            librarianEmail: settings?.librarianEmail,
          }
        });

        showNotification(`Closure saved - notifications scheduled for ${closureForm.notifyOption === '2weeks' ? '2 weeks' : '1 week'} before`, 'success');
        setShowClosureModal(false);
        await loadBookings();
        await loadClosures();
        return;
      }

      // For immediate notifications, send now
      if (closureForm.notifyOption === 'immediate') {
        const emailPromises = [];

        // 1. Send to affected teachers (closure emails)
        for (const booking of affectedBookings) {
          if (booking.teacherEmail) {
            emailPromises.push(
              sendBookingEmail({
                type: 'closure',
                to: booking.teacherEmail,
                teacherName: booking.teacherName,
                bookingDate: booking.date,
                bookingTime: `${formatTime12(booking.startTime)} - ${formatTime12(booking.endTime)}`,
                library: booking.library,
                grade: booking.grade,
                librarianName: settings?.yourName,
                schoolName: settings?.schoolName,
                closureReason: reasonLabel,
                closureMessage: closureForm.customMessage,
                replyTo: settings?.librarianEmail
              })
            );
          }
        }

        // 2. Send to aides (if day type selected and notifyAides is true)
        if (closureForm.dayType && closureForm.dayType !== 'na' && closureForm.notifyAides) {
          const aidesForDay = getAidesForDayType(closureForm.dayType);
          for (const aide of aidesForDay) {
            if (aide.email) {
              emailPromises.push(
                sendAideClosureEmail({
                  aideEmail: aide.email,
                  aideName: aide.name,
                  dayType: closureForm.dayType,
                  closureDate: closureForm.startDate,
                  closureReason: reasonLabel,
                  aideWorkAssignment: closureForm.aideWorkAssignment || '',
                  aideReportTo: closureForm.aideReportTo || '',
                  librarianName: settings?.yourName,
                  schoolName: settings?.schoolName,
                  librarianEmail: settings?.librarianEmail,
                })
              );
            }
          }
        }

        // 3. Send to library assistant (if full day and enabled)
        if (closureForm.timeType === 'full' && closureForm.notifyLibraryAssistant) {
          const assistant = getLibraryAssistant();
          if (assistant && assistant.email) {
            emailPromises.push(
              sendLibraryAssistantClosureEmail({
                assistantEmail: assistant.email,
                assistantName: assistant.name,
                closureDate: closureForm.startDate,
                closureDateEnd: closureForm.dateType === 'range' ? closureForm.endDate : closureForm.startDate,
                closureReason: reasonLabel,
                library: closureForm.library,
                librarianName: settings?.yourName,
                schoolName: settings?.schoolName,
                librarianEmail: settings?.librarianEmail,
              })
            );
          }
        }

        // 4. Send to library chairs (if enabled and configured for emergency closures)
        if (closureForm.notifyLibraryChairs && settingsForm.libraryChairSettings?.notifyOnEmergencyClosure) {
          const chairs = getLibraryChairs();
          for (const chair of chairs) {
            if (chair.email) {
              emailPromises.push(
                sendLibraryChairsClosureEmail({
                  chairEmail: chair.email,
                  chairName: chair.name,
                  closureDate: closureForm.startDate,
                  closureReason: reasonLabel,
                  library: closureForm.library,
                  affectedClassCount: affectedBookings.length,
                  customMessage: closureForm.customMessage || '',
                  librarianName: settings?.yourName,
                  schoolName: settings?.schoolName,
                  librarianEmail: settings?.librarianEmail,
                })
              );
            }
          }
        }

        // 5. Send to recess teachers (per affected recess schedule)
        if (closureForm.library === 'both') {
          const affected = getAffectedRecesses(
            closureForm.startTime || '00:00',
            closureForm.endTime || '23:59',
            closureForm.library,
            closureForm.timeType
          );
          const notifyMap = closureForm.notifyRecessMap || {};

          for (const recess of affected) {
            if (notifyMap[recess.id] === false) continue;
            const recessTeachersList = getTeachersForRecess(recess);

            for (const teacher of recessTeachersList) {
              if (teacher.email) {
                emailPromises.push(
                  sendRecessCancellationEmail({
                    teacherEmail: teacher.email,
                    teacherName: teacher.name,
                    closureDate: closureForm.startDate,
                    closureReason: reasonLabel,
                    recessStartTime: recess.startTime || '',
                    recessEndTime: recess.endTime || '',
                    librarianName: settings?.yourName,
                    schoolName: settings?.schoolName,
                    librarianEmail: settings?.librarianEmail,
                  })
                );
              }
            }
          }
        }

        // 6. Send to admin contacts (CC)
        if (closureForm.adminContactIds && closureForm.adminContactIds.length > 0) {
          const allAdminContacts = settingsForm.adminContacts || [];
          const selectedContacts = allAdminContacts.filter(c =>
            closureForm.adminContactIds.includes(c.id)
          );

          for (const contact of selectedContacts) {
            if (contact.email) {
              emailPromises.push(
                sendAdminClosureCCEmail({
                  contactEmail: contact.email,
                  contactName: contact.name,
                  closureDate: closureForm.startDate,
                  closureDateEnd: closureForm.dateType === 'range' ? closureForm.endDate : closureForm.startDate,
                  closureReason: reasonLabel,
                  library: closureForm.library,
                  timeType: closureForm.timeType,
                  startTime: closureForm.startTime,
                  endTime: closureForm.endTime,
                  affectedClassCount: affectedBookings.length,
                  customMessage: closureForm.customMessage || '',
                  librarianName: settings?.yourName,
                  schoolName: settings?.schoolName,
                  librarianEmail: settings?.librarianEmail,
                })
              );
            }
          }
        }

        // 7. Send to additional one-time CC (if provided)
        if (closureForm.additionalCcEmail && isValidEmail(closureForm.additionalCcEmail)) {
          emailPromises.push(
            sendAdminClosureCCEmail({
              contactEmail: closureForm.additionalCcEmail,
              contactName: 'CC',
              closureDate: closureForm.startDate,
              closureDateEnd: closureForm.dateType === 'range' ? closureForm.endDate : closureForm.startDate,
              closureReason: reasonLabel,
              library: closureForm.library,
              timeType: closureForm.timeType,
              startTime: closureForm.startTime,
              endTime: closureForm.endTime,
              affectedClassCount: affectedBookings.length,
              customMessage: closureForm.customMessage || '',
              librarianName: settings?.yourName,
              schoolName: settings?.schoolName,
              librarianEmail: settings?.librarianEmail,
            })
          );
        }

        // Execute all email sends
        try {
          const results = await Promise.allSettled(emailPromises);
          const successCount = results.filter(r => r.status === 'fulfilled' && r.value?.success).length;
          const failCount = results.length - successCount;

          if (failCount > 0) {
            showNotification(`Closure saved. ${successCount} emails sent, ${failCount} failed.`, 'warning');
          } else if (successCount > 0) {
            showNotification(`Closure saved. ${successCount} notification${successCount !== 1 ? 's' : ''} sent.`, 'success');
          } else {
            showNotification('Closure saved!', 'success');
          }
        } catch (emailError) {
          console.error('Error sending closure emails:', emailError);
          showNotification('Closure saved but some emails failed to send.', 'warning');
        }
      }

      setShowClosureModal(false);
      await loadBookings();
      await loadClosures();
    } catch (err) {
      console.error('Error saving closure:', err);
      showNotification('Failed to save closure', 'error');
    }
  };

  // Helper function for weekend check (uses getDayOfWeekFromDate from context)
  const isWeekend = (dateStr) => {
    const day = getDayOfWeekFromDate(dateStr);
    return day === 'Saturday' || day === 'Sunday';
  };

  // Check if previous year data exists
  const hasPreviousYearData = settings?.academicYear?.isSetupComplete &&
    settings?.academicYear?.startDate;

  // Get previous year label for display
  const previousYearLabel = settings?.academicYear?.startDate
    ? `${settings.academicYear.startDate.split('-')[0]}-${settings.academicYear.endDate?.split('-')[0] || ''}`
    : '';

  // Copy calendar data from previous year with date shifting
  const copyFromPreviousYear = async (newYearStart) => {
    if (!currentUser || !settings?.academicYear?.startDate) {
      showNotification('No previous year data found to copy', 'error');
      return false;
    }

    setIsCopyingYear(true);

    try {
      // Extract the starting year from the new academic year
      const newYear = parseInt(newYearStart.split('-')[0]);

      // Shift holy days
      const shiftedHolyDays = holyDays.map(hd => ({
        name: hd.name,
        date: shiftDateToNewYear(hd.date, newYear)
      }));
      setWizardHolyDays(shiftedHolyDays);

      // Shift adoration days (must remain Fridays)
      const shiftedAdorationDays = adorationDays.map(ad => {
        const newDate = shiftDateToNewYear(ad.date, newYear);
        return { date: newDate };
      }).filter(ad => isDateOnDayOfWeek(ad.date, 5)); // 5 = Friday
      setWizardAdorationDays(shiftedAdorationDays);

      // Separate school calendar events by type
      const holidays = schoolCalendarEvents.filter(e => e.type === 'holiday');
      const halfDays = schoolCalendarEvents.filter(e => e.type === 'half-day');
      const workdays = schoolCalendarEvents.filter(e => e.type === 'workday');
      const libraryEvents = schoolCalendarEvents.filter(e =>
        ['book-fair', 'tla', 'diocesan-meeting', 'inventory', 'custom'].includes(e.type)
      );

      // Shift holidays (including extended)
      const shiftedHolidays = holidays.map(h => {
        const shifted = shiftDateRangeToNewYear(h.startDate, h.endDate || h.startDate, newYear);
        return {
          name: h.name,
          startDate: shifted.startDate,
          endDate: shifted.endDate,
          type: h.startDate !== (h.endDate || h.startDate) ? 'extended' : undefined
        };
      });
      setWizardHolidays(shiftedHolidays);

      // Shift half days
      const shiftedHalfDays = halfDays.map(h => ({
        name: h.name,
        date: shiftDateToNewYear(h.startDate, newYear),
        period: h.halfDayPeriod || 'afternoon'
      }));
      setWizardHalfDays(shiftedHalfDays);

      // Shift workdays
      const shiftedWorkdays = workdays.map(w => {
        const shifted = shiftDateRangeToNewYear(w.startDate, w.endDate || w.startDate, newYear);
        return {
          name: w.name,
          startDate: shifted.startDate,
          endDate: shifted.endDate
        };
      });
      setWizardWorkdays(shiftedWorkdays);

      // Shift library events
      const shiftedLibraryEvents = libraryEvents.map(e => {
        const shifted = shiftDateRangeToNewYear(e.startDate, e.endDate || e.startDate, newYear);
        return {
          eventType: e.type,
          name: e.name,
          startDate: shifted.startDate,
          endDate: shifted.endDate
        };
      });
      setWizardLibraryEvents(shiftedLibraryEvents);

      // Shift Saturday openings (must remain Saturdays)
      const shiftedSaturdays = saturdayOpenings.map(s => {
        const newDate = shiftDateToNewYear(s.date, newYear);
        if (!isDateOnDayOfWeek(newDate, 6)) return null; // 6 = Saturday
        return {
          date: newDate,
          description: s.description || ''
        };
      }).filter(Boolean);

      setWizardSaturdayConfig({
        enabled: saturdaySchedule?.enabled || false,
        startTime: saturdaySchedule?.startTime || '09:00',
        endTime: saturdaySchedule?.endTime || '13:00',
        dates: shiftedSaturdays
      });

      // Copy mass schedule (day/times don't change year-to-year)
      setYearSetupForm(prev => ({
        ...prev,
        massDay: settings?.massSchedule?.regularDay || 'Thursday',
        massStartTime: settings?.massSchedule?.startTime || '09:00',
        massEndTime: settings?.massSchedule?.endTime || '10:00'
      }));

      showNotification('Copied data from previous year. Dates have been shifted to match the same day-of-week.', 'success');
      return true;
    } catch (err) {
      console.error('Error copying previous year data:', err);
      showNotification('Failed to copy previous year data', 'error');
      return false;
    } finally {
      setIsCopyingYear(false);
    }
  };

  // Save academic year setup
  const handleSaveYearSetup = async () => {
    try {
      console.log('💾 Saving year setup...');
      console.log('yearSetupForm:', yearSetupForm);
      console.log('wizardSaturdayConfig:', wizardSaturdayConfig);

      const settingsRef = doc(db, 'users', currentUser.uid, 'settings', 'nornsWeave');
      const existingSettings = settings || {};

      const dataToSave = {
        ...existingSettings,
        academicYear: {
          startDate: yearSetupForm.academicYearStart,
          endDate: yearSetupForm.academicYearEnd,
          schoolStartDate: yearSetupForm.schoolStartDate,
          schoolEndDate: yearSetupForm.schoolEndDate,
          isSetupComplete: true,
          isLocked: true,
          setupCompletedAt: new Date().toISOString(),
          lastModifiedAt: new Date().toISOString()
        },
        massSchedule: {
          regularDay: yearSetupForm.massDay,
          startTime: yearSetupForm.massStartTime,
          endTime: yearSetupForm.massEndTime
        },
        saturdaySchedule: {
          enabled: wizardSaturdayConfig.enabled || false,
          startTime: wizardSaturdayConfig.startTime || '09:00',
          endTime: wizardSaturdayConfig.endTime || '13:00'
          // Note: dates are now saved to nornsWeaveSaturdayOpenings collection
        }
      };

      console.log('Data being saved to Firebase:', dataToSave);
      await setDoc(settingsRef, dataToSave, { merge: true });
      console.log('✅ Data saved to Firebase successfully');

      // Update local Saturday schedule state (config only, dates are in collection)
      setSaturdaySchedule({
        enabled: wizardSaturdayConfig.enabled || false,
        startTime: wizardSaturdayConfig.startTime || '09:00',
        endTime: wizardSaturdayConfig.endTime || '13:00'
      });

      // Save holy days - check for existing first to prevent duplicates
      const existingHolyDaysSnap = await getDocs(collection(db, 'users', currentUser.uid, 'nornsWeaveHolyDays'));
      const existingHolyDates = new Set(existingHolyDaysSnap.docs.map(d => d.data().date));

      for (const holyDay of wizardHolyDays) {
        if (holyDay.date && holyDay.name && !existingHolyDates.has(holyDay.date)) {
          await addDoc(collection(db, 'users', currentUser.uid, 'nornsWeaveHolyDays'), {
            ...holyDay,
            dayOfWeek: getDayOfWeekFromDate(holyDay.date),
            shiftsMass: getDayOfWeekFromDate(holyDay.date) !== yearSetupForm.massDay && !isWeekend(holyDay.date),
            notificationsSent: { twoWeeks: false, oneWeek: false },
            createdAt: new Date().toISOString()
          });
          existingHolyDates.add(holyDay.date); // Prevent duplicates within same wizard run
        }
      }

      // Save adoration days - check for existing first to prevent duplicates
      const existingAdorationSnap = await getDocs(collection(db, 'users', currentUser.uid, 'nornsWeaveAdorationDays'));
      const existingAdorationDates = new Set(existingAdorationSnap.docs.map(d => d.data().date));

      for (const adorationDay of wizardAdorationDays) {
        if (adorationDay.date && !existingAdorationDates.has(adorationDay.date)) {
          await addDoc(collection(db, 'users', currentUser.uid, 'nornsWeaveAdorationDays'), {
            date: adorationDay.date,
            dayOfWeek: 'Friday',
            notificationsSent: { twoWeeks: false, oneWeek: false },
            createdAt: new Date().toISOString()
          });
          existingAdorationDates.add(adorationDay.date); // Prevent duplicates within same wizard run
        }
      }

      // Save calendar events - check for existing first to prevent duplicates
      // Use type-startDate key (without name) to prevent duplicates even if name differs slightly
      const existingCalendarSnap = await getDocs(collection(db, 'users', currentUser.uid, 'nornsWeaveSchoolCalendar'));
      const existingCalendarKeys = new Set(existingCalendarSnap.docs.map(d => {
        const data = d.data();
        return `${data.type}-${data.startDate}`;
      }));

      // Save holidays
      for (const holiday of wizardHolidays) {
        const key = `holiday-${holiday.startDate}`;
        if (holiday.name && holiday.startDate && !existingCalendarKeys.has(key)) {
          await addDoc(collection(db, 'users', currentUser.uid, 'nornsWeaveSchoolCalendar'), {
            type: 'holiday',
            name: holiday.name,
            startDate: holiday.startDate,
            endDate: holiday.endDate || holiday.startDate,
            libraryStatus: 'closed',
            notificationsSent: { twoWeeks: false, oneWeek: false },
            createdAt: new Date().toISOString()
          });
          existingCalendarKeys.add(key); // Prevent duplicates within same wizard run
        }
      }

      // Save half days
      for (const halfDay of wizardHalfDays) {
        const key = `half-day-${halfDay.date}`;
        if (halfDay.name && halfDay.date && !existingCalendarKeys.has(key)) {
          await addDoc(collection(db, 'users', currentUser.uid, 'nornsWeaveSchoolCalendar'), {
            type: 'half-day',
            name: halfDay.name,
            startDate: halfDay.date,
            endDate: halfDay.date,
            halfDayPeriod: halfDay.period,
            libraryStatus: 'limited',
            notificationsSent: { twoWeeks: false, oneWeek: false },
            createdAt: new Date().toISOString()
          });
          existingCalendarKeys.add(key); // Prevent duplicates within same wizard run
        }
      }

      // Save workdays
      // NOTE: Workdays ALWAYS have libraryStatus: 'open' - they are NOT closures
      // Bookings are allowed on workdays, just fixed classes are hidden
      for (const workday of wizardWorkdays) {
        const key = `workday-${workday.startDate}`;
        if (workday.name && workday.startDate && !existingCalendarKeys.has(key)) {
          await addDoc(collection(db, 'users', currentUser.uid, 'nornsWeaveSchoolCalendar'), {
            type: 'workday',
            name: workday.name,
            startDate: workday.startDate,
            endDate: workday.endDate || workday.startDate,
            libraryStatus: 'open', // Workdays are ALWAYS open for bookings
            youWorking: true,
            notificationsSent: { twoWeeks: false, oneWeek: false },
            createdAt: new Date().toISOString()
          });
          existingCalendarKeys.add(key); // Prevent duplicates within same wizard run
        }
      }

      // Save library events (Book Fair, TLA, Diocesan Meetings, etc.)
      // These block the library for the date range
      for (const event of wizardLibraryEvents) {
        const key = `${event.eventType}-${event.startDate}`;
        if (event.name && event.startDate && !existingCalendarKeys.has(key)) {
          await addDoc(collection(db, 'users', currentUser.uid, 'nornsWeaveSchoolCalendar'), {
            type: event.eventType, // 'book-fair', 'tla', 'diocesan-meeting', 'other'
            name: event.name,
            startDate: event.startDate,
            endDate: event.endDate || event.startDate,
            libraryStatus: 'closed', // Library events block bookings
            notificationsSent: { twoWeeks: false, oneWeek: false },
            createdAt: new Date().toISOString()
          });
          existingCalendarKeys.add(key); // Prevent duplicates within same wizard run
        }
      }

      // Save Saturday openings to collection - check for existing first to prevent duplicates
      const existingSaturdaySnap = await getDocs(collection(db, 'users', currentUser.uid, 'nornsWeaveSaturdayOpenings'));
      const existingSaturdayDates = new Set(existingSaturdaySnap.docs.map(d => d.data().date));

      for (const saturdayItem of (wizardSaturdayConfig.dates || [])) {
        const satDate = typeof saturdayItem === 'string' ? saturdayItem : saturdayItem.date;
        const satDescription = typeof saturdayItem === 'object' ? (saturdayItem.description || '') : '';

        if (satDate && !existingSaturdayDates.has(satDate)) {
          await addDoc(collection(db, 'users', currentUser.uid, 'nornsWeaveSaturdayOpenings'), {
            date: satDate,
            description: satDescription,
            dayOfWeek: 'Saturday',
            createdAt: new Date().toISOString()
          });
          existingSaturdayDates.add(satDate); // Prevent duplicates within same wizard run
        }
      }

      // Reload data
      await loadYearSetupData();
      await loadSettings();

      setShowYearSetupWizard(false);
      setYearSetupStep(1);
      setIsYearSetupLocked(true);
      showNotification('Academic year setup complete!', 'success');
    } catch (err) {
      console.error('Error saving year setup:', err);
      showNotification('Error saving year setup', 'error');
    }
  };

  // Toggle year setup lock
  const toggleYearSetupLock = async () => {
    try {
      const settingsRef = doc(db, 'users', currentUser.uid, 'settings', 'nornsWeave');
      await setDoc(settingsRef, {
        academicYear: {
          ...settings?.academicYear,
          isLocked: !isYearSetupLocked,
          lastModifiedAt: new Date().toISOString()
        }
      }, { merge: true });
      setIsYearSetupLocked(!isYearSetupLocked);
      showNotification(isYearSetupLocked ? 'Calendar unlocked for editing' : 'Calendar locked', 'success');
    } catch (err) {
      console.error('Error toggling lock:', err);
      showNotification('Error updating lock status', 'error');
    }
  };

  // Delete a holy day
  const handleDeleteHolyDay = async (holyDayId) => {
    if (!window.confirm('Are you sure you want to delete this holy day?')) return;
    try {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveHolyDays', holyDayId));
      await loadYearSetupData();
      showNotification('Holy day deleted', 'success');
    } catch (err) {
      console.error('Error deleting holy day:', err);
      showNotification('Error deleting holy day', 'error');
    }
  };

  // Delete an adoration day
  const handleDeleteAdorationDay = async (adorationId) => {
    if (!window.confirm('Are you sure you want to delete this adoration day?')) return;
    try {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveAdorationDays', adorationId));
      await loadYearSetupData();
      showNotification('Adoration day deleted', 'success');
    } catch (err) {
      console.error('Error deleting adoration day:', err);
      showNotification('Error deleting adoration day', 'error');
    }
  };

  // Delete a school calendar event
  const handleDeleteSchoolCalendarEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveSchoolCalendar', eventId));
      await loadYearSetupData();
      showNotification('Event deleted', 'success');
    } catch (err) {
      console.error('Error deleting event:', err);
      showNotification('Error deleting event', 'error');
    }
  };

  // Edit a holy day
  const handleEditHolyDay = async (holyDayId, name, date) => {
    try {
      const massDay = yearSetupForm.massDay || 'Thursday';
      const dayOfWeek = getDayOfWeekFromDate(date);
      const isWeekendDay = dayOfWeek === 'Saturday' || dayOfWeek === 'Sunday';
      const shiftsMass = dayOfWeek !== massDay && !isWeekendDay;

      await updateDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveHolyDays', holyDayId), {
        name,
        date,
        shiftsMass
      });
      await loadYearSetupData();
      showNotification('Holy day updated', 'success');
    } catch (err) {
      console.error('Error updating holy day:', err);
      showNotification('Error updating holy day', 'error');
    }
  };

  // Edit an adoration day
  const handleEditAdorationDay = async (adorationId, date) => {
    try {
      await updateDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveAdorationDays', adorationId), {
        date
      });
      await loadYearSetupData();
      showNotification('Adoration day updated', 'success');
    } catch (err) {
      console.error('Error updating adoration day:', err);
      showNotification('Error updating adoration day', 'error');
    }
  };

  // Edit a school calendar event
  const handleEditSchoolCalendarEvent = async (eventId, updates) => {
    try {
      await updateDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveSchoolCalendar', eventId), updates);
      await loadYearSetupData();
      showNotification('Event updated', 'success');
    } catch (err) {
      console.error('Error updating event:', err);
      showNotification('Error updating event', 'error');
    }
  };

  // Delete a mass time block
  const handleDeleteMassTimeBlock = async (blockId) => {
    if (!window.confirm('Are you sure you want to delete this time block?')) return;
    try {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveMassTimeBlocks', blockId));
      await loadYearSetupData();
      showNotification('Time block deleted', 'success');
    } catch (err) {
      console.error('Error deleting time block:', err);
      showNotification('Error deleting time block', 'error');
    }
  };

  // ============================================
  // DUPLICATE CLEANUP UTILITIES
  // ============================================

  // Find duplicates in a collection based on a key field
  const findDuplicates = (items, keyField) => {
    const seen = new Map();
    const duplicates = [];

    items.forEach(item => {
      const key = typeof keyField === 'function' ? keyField(item) : item[keyField];
      if (seen.has(key)) {
        duplicates.push(item);
      } else {
        seen.set(key, item);
      }
    });

    return {
      unique: Array.from(seen.values()),
      duplicates,
      duplicateCount: duplicates.length
    };
  };

  // Master cleanup function - clean all duplicates
  const cleanupAllDuplicates = async () => {
    // Use simpler key for calendar events - just type and startDate (ignore name differences)
    const holyDayDups = findDuplicates(holyDays, 'date').duplicateCount;
    const adorationDups = findDuplicates(adorationDays, 'date').duplicateCount;
    const calendarDups = findDuplicates(schoolCalendarEvents, (item) => `${item.type}-${item.startDate}`).duplicateCount;

    const total = holyDayDups + adorationDups + calendarDups;

    if (total === 0) {
      showNotification('No duplicates found in any collection!', 'success');
      return;
    }

    const message = `Found duplicates:\n` +
      `- Holy Days: ${holyDayDups}\n` +
      `- Adoration Days: ${adorationDups}\n` +
      `- Calendar Events: ${calendarDups}\n\n` +
      `Total: ${total} duplicates\n\nDelete all duplicates?`;

    if (!window.confirm(message)) {
      return;
    }

    let deleted = 0;

    // Delete without individual confirmations
    for (const dup of findDuplicates(holyDays, 'date').duplicates) {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveHolyDays', dup.id));
      deleted++;
    }

    for (const dup of findDuplicates(adorationDays, 'date').duplicates) {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveAdorationDays', dup.id));
      deleted++;
    }

    for (const dup of findDuplicates(schoolCalendarEvents, (item) => `${item.type}-${item.startDate}`).duplicates) {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveSchoolCalendar', dup.id));
      deleted++;
    }

    await loadYearSetupData();
    showNotification(`Successfully deleted ${deleted} duplicates!`, 'success');
  };

  // Fix workday entries that have wrong libraryStatus (should be 'open' not 'closed')
  const fixWorkdayStatuses = async () => {
    try {
      const calendarRef = collection(db, 'users', currentUser.uid, 'nornsWeaveSchoolCalendar');
      const snapshot = await getDocs(calendarRef);

      let fixedCount = 0;
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        // Fix workdays that incorrectly have libraryStatus: 'closed'
        if (data.type === 'workday' && data.libraryStatus !== 'open') {
          await updateDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveSchoolCalendar', docSnap.id), {
            libraryStatus: 'open'
          });
          fixedCount++;
          console.log('Fixed workday:', data.name, '- changed libraryStatus to open');
        }
      }

      if (fixedCount > 0) {
        await loadYearSetupData();
        showNotification(`Fixed ${fixedCount} workday entries`, 'success');
      } else {
        showNotification('All workdays already have correct status', 'success');
      }
    } catch (error) {
      console.error('Error fixing workdays:', error);
      showNotification('Error: ' + error.message, 'error');
    }
  };

  // Clear all calendar data - wipe everything to start from scratch
  const clearAllCalendarData = async () => {
    const warningMessage = `⚠️ WARNING: This will permanently delete ALL calendar data:\n\n` +
      `- All Holy Days (${holyDays.length} items)\n` +
      `- All Adoration Days (${adorationDays.length} items)\n` +
      `- All School Calendar Events (${schoolCalendarEvents.length} items)\n` +
      `- All Saturday Openings (${saturdayOpenings.length} items)\n` +
      `- All Mass Time Blocks (${massTimeBlocks.length} items)\n` +
      `- Academic Year Settings\n` +
      `- Mass Schedule Settings\n` +
      `- Saturday Schedule Settings\n\n` +
      `This action CANNOT be undone!\n\n` +
      `Are you absolutely sure you want to proceed?`;

    if (!window.confirm(warningMessage)) {
      return;
    }

    // Double confirmation
    if (!window.confirm('This is your last chance to cancel. Delete ALL calendar data?')) {
      return;
    }

    try {
      let deletedCount = 0;

      // Delete all Holy Days
      const holyDaysRef = collection(db, 'users', currentUser.uid, 'nornsWeaveHolyDays');
      const holyDaysSnap = await getDocs(holyDaysRef);
      for (const docSnap of holyDaysSnap.docs) {
        await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveHolyDays', docSnap.id));
        deletedCount++;
      }

      // Delete all Adoration Days
      const adorationRef = collection(db, 'users', currentUser.uid, 'nornsWeaveAdorationDays');
      const adorationSnap = await getDocs(adorationRef);
      for (const docSnap of adorationSnap.docs) {
        await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveAdorationDays', docSnap.id));
        deletedCount++;
      }

      // Delete all School Calendar Events
      const calendarRef = collection(db, 'users', currentUser.uid, 'nornsWeaveSchoolCalendar');
      const calendarSnap = await getDocs(calendarRef);
      for (const docSnap of calendarSnap.docs) {
        await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveSchoolCalendar', docSnap.id));
        deletedCount++;
      }

      // Delete all Saturday Openings
      const saturdayRef = collection(db, 'users', currentUser.uid, 'nornsWeaveSaturdayOpenings');
      const saturdaySnap = await getDocs(saturdayRef);
      for (const docSnap of saturdaySnap.docs) {
        await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveSaturdayOpenings', docSnap.id));
        deletedCount++;
      }

      // Delete all Mass Time Blocks
      const massBlocksRef = collection(db, 'users', currentUser.uid, 'nornsWeaveMassTimeBlocks');
      const massBlocksSnap = await getDocs(massBlocksRef);
      for (const docSnap of massBlocksSnap.docs) {
        await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveMassTimeBlocks', docSnap.id));
        deletedCount++;
      }

      // Reset settings document - clear academic year, mass schedule, and saturday schedule
      const settingsRef = doc(db, 'users', currentUser.uid, 'settings', 'nornsWeave');
      await setDoc(settingsRef, {
        academicYear: {
          startDate: '',
          endDate: '',
          schoolStartDate: '',
          schoolEndDate: '',
          isLocked: false
        },
        massSchedule: {
          regularDay: '',
          startTime: '',
          endTime: ''
        },
        saturdaySchedule: {
          enabled: false,
          startTime: '09:00',
          endTime: '13:00'
        },
        lastModifiedAt: new Date().toISOString()
      }, { merge: true });

      await loadYearSetupData();
      showNotification(`Successfully cleared all calendar data! Deleted ${deletedCount} items.`, 'success');
    } catch (error) {
      console.error('Error clearing calendar data:', error);
      showNotification('Error clearing data: ' + error.message, 'error');
    }
  };

  // Helper: Check if two time ranges overlap
  const doTimesOverlapCheck = (start1, end1, start2, end2) => {
    return start1 < end2 && end1 > start2;
  };

  // Helper: Find bookings affected by a date/time range
  const findAffectedBookingsByTime = (date, startTime, endTime) => {
    return bookings.filter(b => {
      if (b.date !== date) return false;
      return doTimesOverlapCheck(b.startTime, b.endTime, startTime, endTime);
    });
  };

  // Helper: Find fixed classes affected by a date and add exception to skip them
  const addFixedClassException = async (date, reason, startTime, endTime) => {
    const dayName = getDayName(date);
    const affectedClasses = fixedClasses.filter(c => {
      if (!matchesDay(c.dayOfWeek, dayName)) return false;
      // If time range provided, check overlap
      if (startTime && endTime) {
        return doTimesOverlapCheck(c.startTime, c.endTime, startTime, endTime);
      }
      return true; // Full day event affects all classes
    });

    for (const fixedClass of affectedClasses) {
      // Check if exception already exists
      const hasException = fixedClass.exceptions?.some(ex => {
        const exDate = typeof ex === 'string' ? ex : ex.date;
        return exDate === date;
      });

      if (!hasException) {
        const updatedExceptions = [
          ...(fixedClass.exceptions || []),
          { date, type: 'skipped', reason }
        ];

        await updateDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveClasses', fixedClass.id), {
          exceptions: updatedExceptions
        });
      }
    }

    return affectedClasses.length;
  };

  // Helper: Cancel affected bookings and send notifications
  const cancelAffectedBookings = async (affectedBookings, reason, eventName) => {
    for (const booking of affectedBookings) {
      // Delete the booking
      await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveBookings', booking.id));

      // Send cancellation email if email is enabled
      if (settings?.emailEnabled && booking.teacherEmail) {
        try {
          await sendBookingCancellationEmail({
            ...booking,
            cancellationReason: `${eventName}: ${reason}`,
            librarianName: settings?.yourName,
            schoolName: settings?.schoolName
          });
        } catch (err) {
          console.error('Error sending cancellation email:', err);
        }
      }
    }
  };

  // Add a new holy day
  const handleAddHolyDay = async (name, date) => {
    // Prevent double-clicks
    if (savingHolyDay) return;
    setSavingHolyDay(true);

    try {
      // Check state first (fast)
      const existingInState = holyDays.find(hd => hd.date === date);
      if (existingInState) {
        showNotification(`A holy day already exists on ${new Date(date + 'T00:00:00').toLocaleDateString()}`, 'error');
        setSavingHolyDay(false);
        return;
      }

      // Double-check Firestore (authoritative)
      const holyDaysRef = collection(db, 'users', currentUser.uid, 'nornsWeaveHolyDays');
      const snapshot = await getDocs(holyDaysRef);
      const existingInDb = snapshot.docs.find(d => d.data().date === date);
      if (existingInDb) {
        showNotification(`A holy day already exists on ${new Date(date + 'T00:00:00').toLocaleDateString()}`, 'error');
        await loadYearSetupData(); // Sync state with DB
        setSavingHolyDay(false);
        return;
      }

      const massStart = yearSetupForm.massStartTime || '09:00';
      const massEnd = yearSetupForm.massEndTime || '10:00';

      // Find affected bookings for the mass time slot
      const affected = findAffectedBookingsByTime(date, massStart, massEnd);

      // Show confirmation if there are affected bookings
      if (affected.length > 0) {
        const confirmCancel = window.confirm(
          `This holy day will affect ${affected.length} booking(s) during mass time (${formatTime12(massStart)} - ${formatTime12(massEnd)}).\n\n` +
          `These bookings will be cancelled and teachers will be notified.\n\nContinue?`
        );
        if (!confirmCancel) {
          setSavingHolyDay(false);
          return;
        }

        // Cancel affected bookings
        await cancelAffectedBookings(affected, `Mass for ${name}`, 'Holy Day');
        await loadBookings();
      }

      // Auto-skip fixed classes during mass time
      const skippedCount = await addFixedClassException(date, `Holy Day: ${name}`, massStart, massEnd);
      if (skippedCount > 0) {
        await loadFixedClasses();
      }

      await addDoc(collection(db, 'users', currentUser.uid, 'nornsWeaveHolyDays'), {
        name,
        date,
        dayOfWeek: getDayOfWeekFromDate(date),
        shiftsMass: getDayOfWeekFromDate(date) !== (settings?.massSchedule?.regularDay || 'Thursday') && !isWeekend(date),
        affectedBookingsCount: affected.length,
        skippedClassesCount: skippedCount,
        notificationsSent: { twoWeeks: false, oneWeek: false },
        createdAt: new Date().toISOString()
      });
      await loadYearSetupData();

      let message = 'Holy day added';
      if (affected.length > 0) message += `, ${affected.length} booking(s) cancelled`;
      if (skippedCount > 0) message += `, ${skippedCount} fixed class(es) will be skipped`;
      showNotification(message, 'success');
    } catch (err) {
      console.error('Error adding holy day:', err);
      showNotification('Error adding holy day', 'error');
    } finally {
      setSavingHolyDay(false);
    }
  };

  // Add a new adoration day
  const handleAddAdorationDay = async (date) => {
    if (getDayOfWeekFromDate(date) !== 'Friday') {
      showNotification('Adoration days must be on Fridays', 'error');
      return;
    }

    // Prevent double-clicks
    if (savingAdorationDay) return;
    setSavingAdorationDay(true);

    try {
      // Check state first (fast)
      const existingInState = adorationDays.find(ad => ad.date === date);
      if (existingInState) {
        showNotification(`An adoration day already exists on ${new Date(date + 'T00:00:00').toLocaleDateString()}`, 'error');
        setSavingAdorationDay(false);
        return;
      }

      // Double-check Firestore (authoritative)
      const adorationRef = collection(db, 'users', currentUser.uid, 'nornsWeaveAdorationDays');
      const snapshot = await getDocs(adorationRef);
      const existingInDb = snapshot.docs.find(d => d.data().date === date);
      if (existingInDb) {
        showNotification(`An adoration day already exists on ${new Date(date + 'T00:00:00').toLocaleDateString()}`, 'error');
        await loadYearSetupData(); // Sync state with DB
        setSavingAdorationDay(false);
        return;
      }

      const massStart = yearSetupForm.massStartTime || '09:00';
      const massEnd = yearSetupForm.massEndTime || '10:00';

      // Find affected bookings for the adoration time slot
      const affected = findAffectedBookingsByTime(date, massStart, massEnd);

      // Show confirmation if there are affected bookings
      if (affected.length > 0) {
        const confirmCancel = window.confirm(
          `This adoration day will affect ${affected.length} booking(s) during adoration time (${formatTime12(massStart)} - ${formatTime12(massEnd)}).\n\n` +
          `These bookings will be cancelled and teachers will be notified.\n\nContinue?`
        );
        if (!confirmCancel) {
          setSavingAdorationDay(false);
          return;
        }

        // Cancel affected bookings
        await cancelAffectedBookings(affected, 'Friday Adoration', 'Adoration Day');
        await loadBookings();
      }

      // Auto-skip fixed classes during adoration time
      const skippedCount = await addFixedClassException(date, 'Friday Adoration', massStart, massEnd);
      if (skippedCount > 0) {
        await loadFixedClasses();
      }

      await addDoc(collection(db, 'users', currentUser.uid, 'nornsWeaveAdorationDays'), {
        date,
        dayOfWeek: 'Friday',
        affectedBookingsCount: affected.length,
        skippedClassesCount: skippedCount,
        notificationsSent: { twoWeeks: false, oneWeek: false },
        createdAt: new Date().toISOString()
      });
      await loadYearSetupData();

      let message = 'Adoration day added';
      if (affected.length > 0) message += `, ${affected.length} booking(s) cancelled`;
      if (skippedCount > 0) message += `, ${skippedCount} fixed class(es) will be skipped`;
      showNotification(message, 'success');
    } catch (err) {
      console.error('Error adding adoration day:', err);
      showNotification('Error adding adoration day', 'error');
    } finally {
      setSavingAdorationDay(false);
    }
  };

  // Add a new school calendar event
  const handleAddSchoolCalendarEvent = async (eventData) => {
    // Prevent double-clicks
    if (savingSchoolCalendarEvent) return;
    setSavingSchoolCalendarEvent(true);

    try {
      const startDate = eventData.startDate;
      const endDate = eventData.endDate || startDate;
      const eventKey = `${eventData.type}-${eventData.name}-${startDate}`;

      // Check state first (fast)
      const existingInState = schoolCalendarEvents.find(e => {
        const eKey = `${e.type}-${e.name}-${e.startDate}`;
        return eKey === eventKey;
      });
      if (existingInState) {
        showNotification(`This ${eventData.type} event already exists`, 'error');
        setSavingSchoolCalendarEvent(false);
        return;
      }

      // Double-check Firestore (authoritative)
      const calendarRef = collection(db, 'users', currentUser.uid, 'nornsWeaveSchoolCalendar');
      const snapshot = await getDocs(calendarRef);
      const existingInDb = snapshot.docs.find(d => {
        const data = d.data();
        return data.type === eventData.type && data.name === eventData.name && data.startDate === startDate;
      });
      if (existingInDb) {
        showNotification(`This ${eventData.type} event already exists`, 'error');
        await loadYearSetupData(); // Sync state with DB
        setSavingSchoolCalendarEvent(false);
        return;
      }

      let totalAffected = 0;
      let totalSkipped = 0;

      // For holidays and closures, cancel all bookings in the date range
      if (eventData.type === 'holiday' || eventData.libraryStatus === 'closed') {
        // Find all bookings in the date range
        const affected = bookings.filter(b => b.date >= startDate && b.date <= endDate);

        if (affected.length > 0) {
          const confirmCancel = window.confirm(
            `This ${eventData.type || 'event'} will affect ${affected.length} booking(s) from ${startDate} to ${endDate}.\n\n` +
            `These bookings will be cancelled and teachers will be notified.\n\nContinue?`
          );
          if (!confirmCancel) {
            setSavingSchoolCalendarEvent(false);
            return;
          }

          await cancelAffectedBookings(affected, eventData.name, eventData.type === 'holiday' ? 'School Holiday' : 'Library Closed');
          await loadBookings();
          totalAffected = affected.length;
        }

        // Auto-skip all fixed classes for each day in range
        let currentDate = new Date(startDate + 'T12:00:00');
        const end = new Date(endDate + 'T12:00:00');
        while (currentDate <= end) {
          // Format date in local timezone to avoid UTC conversion issues
          const year = currentDate.getFullYear();
          const month = String(currentDate.getMonth() + 1).padStart(2, '0');
          const day = String(currentDate.getDate()).padStart(2, '0');
          const dateStr = `${year}-${month}-${day}`;
          const skipped = await addFixedClassException(dateStr, `${eventData.type === 'holiday' ? 'School Holiday' : 'Library Closed'}: ${eventData.name}`, null, null);
          totalSkipped += skipped;
          currentDate.setDate(currentDate.getDate() + 1);
        }
        if (totalSkipped > 0) await loadFixedClasses();
      } else if (eventData.type === 'half-day') {
        // For half days, cancel bookings in the affected period
        const isAfternoon = eventData.halfDayPeriod === 'afternoon';
        const blockStart = isAfternoon ? '12:00' : '07:00';
        const blockEnd = isAfternoon ? '17:00' : '12:00';

        const affected = findAffectedBookingsByTime(startDate, blockStart, blockEnd);

        if (affected.length > 0) {
          const confirmCancel = window.confirm(
            `This half day will affect ${affected.length} booking(s) in the ${eventData.halfDayPeriod}.\n\n` +
            `These bookings will be cancelled and teachers will be notified.\n\nContinue?`
          );
          if (!confirmCancel) {
            setSavingSchoolCalendarEvent(false);
            return;
          }

          await cancelAffectedBookings(affected, `${eventData.name} (${eventData.halfDayPeriod} dismissal)`, 'Half Day');
          await loadBookings();
          totalAffected = affected.length;
        }

        // Auto-skip fixed classes in the affected period
        totalSkipped = await addFixedClassException(startDate, `Half Day: ${eventData.name}`, blockStart, blockEnd);
        if (totalSkipped > 0) await loadFixedClasses();
      }

      // IMPORTANT: Workdays ALWAYS have libraryStatus: 'open' regardless of what was passed in
      // Workdays are NOT closures - they allow bookings, just hide fixed classes
      const finalLibraryStatus = eventData.type === 'workday' ? 'open' : eventData.libraryStatus;

      await addDoc(collection(db, 'users', currentUser.uid, 'nornsWeaveSchoolCalendar'), {
        ...eventData,
        libraryStatus: finalLibraryStatus,
        affectedBookingsCount: totalAffected,
        skippedClassesCount: totalSkipped,
        notificationsSent: { twoWeeks: false, oneWeek: false },
        createdAt: new Date().toISOString()
      });
      await loadYearSetupData();

      let message = 'Event added';
      if (totalAffected > 0) message += `, ${totalAffected} booking(s) cancelled`;
      if (totalSkipped > 0) message += `, ${totalSkipped} fixed class(es) will be skipped`;
      showNotification(message, 'success');
    } catch (err) {
      console.error('Error adding event:', err);
      showNotification('Error adding event', 'error');
    } finally {
      setSavingSchoolCalendarEvent(false);
    }
  };

  // Add a new Saturday opening
  const handleAddSaturdayOpening = async (date, description = '') => {
    // Prevent double-clicks
    if (savingSchoolCalendarEvent) return; // Reuse this flag
    setSavingSchoolCalendarEvent(true);

    try {
      // Verify it's a Saturday
      if (getDayOfWeekFromDate(date) !== 'Saturday') {
        showNotification('Saturday openings must be on Saturdays', 'error');
        setSavingSchoolCalendarEvent(false);
        return;
      }

      // Check if already exists
      const saturdayRef = collection(db, 'users', currentUser.uid, 'nornsWeaveSaturdayOpenings');
      const snapshot = await getDocs(saturdayRef);
      const existingInDb = snapshot.docs.find(d => d.data().date === date);
      if (existingInDb) {
        showNotification(`Saturday opening already exists on ${new Date(date + 'T00:00:00').toLocaleDateString()}`, 'error');
        setSavingSchoolCalendarEvent(false);
        return;
      }

      await addDoc(collection(db, 'users', currentUser.uid, 'nornsWeaveSaturdayOpenings'), {
        date,
        description: description || '',
        dayOfWeek: 'Saturday',
        createdAt: new Date().toISOString()
      });
      await loadYearSetupData();
      showNotification('Saturday opening added', 'success');
    } catch (err) {
      console.error('Error adding Saturday opening:', err);
      showNotification('Error adding Saturday opening', 'error');
    } finally {
      setSavingSchoolCalendarEvent(false);
    }
  };

  // Delete a Saturday opening
  const handleDeleteSaturdayOpening = async (saturdayId) => {
    if (!window.confirm('Are you sure you want to delete this Saturday opening?')) return;
    try {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveSaturdayOpenings', saturdayId));
      await loadYearSetupData();
      showNotification('Saturday opening deleted', 'success');
    } catch (err) {
      console.error('Error deleting Saturday opening:', err);
      showNotification('Error deleting Saturday opening', 'error');
    }
  };

  // Edit a Saturday opening
  const handleEditSaturdayOpening = async (saturdayId, date, description) => {
    try {
      // Verify it's a Saturday
      if (getDayOfWeekFromDate(date) !== 'Saturday') {
        showNotification('Saturday openings must be on Saturdays', 'error');
        return;
      }

      await updateDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveSaturdayOpenings', saturdayId), {
        date,
        description: description || ''
      });
      await loadYearSetupData();
      showNotification('Saturday opening updated', 'success');
    } catch (err) {
      console.error('Error updating Saturday opening:', err);
      showNotification('Error updating Saturday opening', 'error');
    }
  };

  // Add a new mass time block
  const handleAddMassTimeBlock = async (date, reason, useMassTime, customStartTime, customEndTime) => {
    // Prevent double-clicks
    if (savingMassBlock) return;
    setSavingMassBlock(true);

    try {
      // Check state first (fast)
      const existingInState = massTimeBlocks.find(mb => mb.date === date);
      if (existingInState) {
        showNotification(`A mass time block already exists on ${new Date(date + 'T00:00:00').toLocaleDateString()}`, 'error');
        setSavingMassBlock(false);
        return;
      }

      // Double-check Firestore (authoritative)
      const blocksRef = collection(db, 'users', currentUser.uid, 'nornsWeaveMassTimeBlocks');
      const snapshot = await getDocs(blocksRef);
      const existingInDb = snapshot.docs.find(d => d.data().date === date);
      if (existingInDb) {
        showNotification(`A mass time block already exists on ${new Date(date + 'T00:00:00').toLocaleDateString()}`, 'error');
        await loadYearSetupData(); // Sync state with DB
        setSavingMassBlock(false);
        return;
      }

      const blockStart = useMassTime ? (yearSetupForm.massStartTime || '09:00') : customStartTime;
      const blockEnd = useMassTime ? (yearSetupForm.massEndTime || '10:00') : customEndTime;

      // Find affected bookings
      const affected = findAffectedBookingsByTime(date, blockStart, blockEnd);

      if (affected.length > 0) {
        const confirmCancel = window.confirm(
          `This time block will affect ${affected.length} booking(s) from ${formatTime12(blockStart)} to ${formatTime12(blockEnd)}.\n\n` +
          `These bookings will be cancelled and teachers will be notified.\n\nContinue?`
        );
        if (!confirmCancel) {
          setSavingMassBlock(false);
          return;
        }

        await cancelAffectedBookings(affected, reason, 'Mass Time Block');
        await loadBookings();
      }

      // Auto-skip fixed classes
      const skippedCount = await addFixedClassException(date, `Mass Block: ${reason}`, blockStart, blockEnd);
      if (skippedCount > 0) await loadFixedClasses();

      await addDoc(collection(db, 'users', currentUser.uid, 'nornsWeaveMassTimeBlocks'), {
        date,
        reason,
        useMassTime,
        customStartTime: useMassTime ? null : customStartTime,
        customEndTime: useMassTime ? null : customEndTime,
        affectedBookingsCount: affected.length,
        skippedClassesCount: skippedCount,
        notificationsSent: {},
        createdAt: new Date().toISOString()
      });
      await loadYearSetupData();

      let message = 'Time block added';
      if (affected.length > 0) message += `, ${affected.length} booking(s) cancelled`;
      if (skippedCount > 0) message += `, ${skippedCount} fixed class(es) will be skipped`;
      showNotification(message, 'success');
    } catch (err) {
      console.error('Error adding time block:', err);
      showNotification('Error adding time block', 'error');
    } finally {
      setSavingMassBlock(false);
    }
  };

  const handleAddFixedClass = async () => {
    // Validate required fields
    if (!newFixedClass.teacherName || !newFixedClass.teacherEmail || !newFixedClass.startTime || !newFixedClass.duration || !newFixedClass.grade) {
      alert('Please fill in all required fields');
      return;
    }

    // Check subject is required for grades 5-8
    if (['5th', '6th', '7th', '8th'].includes(newFixedClass.grade) && !newFixedClass.subject) {
      alert('Please select a subject for middle school grades');
      return;
    }

    // Calculate end time from duration
    let endTime = newFixedClass.endTime;
    if (!endTime && newFixedClass.startTime && newFixedClass.duration) {
      const [hours, mins] = newFixedClass.startTime.split(':').map(Number);
      const duration = parseInt(newFixedClass.duration);
      const totalMins = hours * 60 + mins + duration;
      const endHours = Math.floor(totalMins / 60);
      const endMins = totalMins % 60;
      endTime = `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
    }
    try {
      const magicLinkToken = uuidv4();
      const classData = {
        ...newFixedClass,
        endTime,
        magicLinkToken,
        exceptions: [],
        createdAt: new Date().toISOString()
      };
      await addDoc(collection(db, 'users', currentUser.uid, 'nornsWeaveClasses'), classData);

      setNewFixedClass({
        teacherName: '',
        teacherEmail: '',
        grade: '',
        subject: '',
        dayOfWeek: 'Monday',
        startTime: '',
        endTime: '',
        duration: '',
        library: 'elementary',
        studentCount: ''
      });
      setShowAddFixedClassModal(false);
      await loadFixedClasses();

      // Ask if they want to email the teacher
      const magicLink = `${window.location.origin}/calendar/booking/${magicLinkToken}`;
      const scheduleText = classData.dayOfWeek === 'everyday'
        ? 'Every school day (Mon-Fri)'
        : `Every ${classData.dayOfWeek}`;
      if (window.confirm(`Fixed class added!\n\nWould you like to email ${classData.teacherName} their permanent booking link?`)) {
        // Send email to teacher with their magic link
        if (settings?.emailEnabled) {
          try {
            await sendBookingEmail({
              type: 'fixed_class_scheduled',
              to: classData.teacherEmail,
              teacherName: classData.teacherName,
              bookingDate: scheduleText,
              bookingTime: `${formatTime12(classData.startTime)} - ${formatTime12(endTime)}`,
              library: classData.library,
              grade: classData.grade,
              librarianName: settings?.yourName,
              schoolName: settings?.schoolName,
              magicLinkUrl: magicLink,
              scheduleText: scheduleText
            });
            alert(`Email sent to ${classData.teacherEmail}!`);
          } catch (emailErr) {
            console.error('Error sending email:', emailErr);
            alert(`Fixed class added but email failed. You can share this link manually:\n\n${magicLink}`);
          }
        } else {
          alert(`Email notifications are disabled. Share this link with the teacher:\n\n${magicLink}`);
        }
      }
    } catch (err) {
      console.error('Error adding fixed class:', err);
      alert(`Error adding fixed class: ${err.message}`);
    }
  };

  const handleAddSingleClass = async () => {
    // Validate required fields
    if (!newSingleClass.teacherName || !newSingleClass.teacherEmail || !newSingleClass.startTime || !newSingleClass.duration || !newSingleClass.grade || !newSingleClass.date) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }

    // Check subject is required for grades 5-8
    if (['5th', '6th', '7th', '8th'].includes(newSingleClass.grade) && !newSingleClass.subject) {
      showNotification('Please select a subject for middle school grades', 'error');
      return;
    }

    // Calculate end time from duration
    let endTime = newSingleClass.endTime;
    if (!endTime && newSingleClass.startTime && newSingleClass.duration) {
      const [hours, mins] = newSingleClass.startTime.split(':').map(Number);
      const duration = parseInt(newSingleClass.duration);
      const totalMins = hours * 60 + mins + duration;
      const endHours = Math.floor(totalMins / 60);
      const endMins = totalMins % 60;
      endTime = `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
    }

    try {
      // Add as a booking (not a fixed class) with the specific date
      const bookingData = {
        teacherName: newSingleClass.teacherName,
        teacherEmail: newSingleClass.teacherEmail,
        grade: newSingleClass.grade,
        subject: newSingleClass.subject || '',
        date: newSingleClass.date,
        startTime: newSingleClass.startTime,
        endTime: endTime,
        library: newSingleClass.library,
        studentCount: newSingleClass.studentCount || '',
        type: newSingleClass.bookingType || 'class', // 'class' or 'admin' - admin bookings persist during no-class days
        status: 'approved',
        createdAt: new Date().toISOString(),
        createdBy: 'admin'
      };

      await addDoc(collection(db, 'users', currentUser.uid, 'nornsWeaveBookings'), bookingData);

      setNewSingleClass({
        teacherName: '',
        teacherEmail: '',
        grade: '',
        subject: '',
        date: '',
        startTime: '',
        endTime: '',
        duration: '',
        library: 'elementary',
        studentCount: '',
        bookingType: 'class'
      });
      setShowAddSingleClassModal(false);
      await loadBookings();
      showNotification('Single class added successfully', 'success');
    } catch (err) {
      console.error('Error adding single class:', err);
      showNotification('Error adding single class', 'error');
    }
  };

  const handleDeleteFixedClass = async (classId) => {
    if (window.confirm('Delete this fixed class?')) {
      try {
        await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveClasses', classId));
        await loadFixedClasses();
      } catch (err) {
        console.error('Error deleting fixed class:', err);
      }
    }
  };

  const handleEditFixedClass = (cls) => {
    setEditingFixedClass({ ...cls });
    setShowEditFixedClassModal(true);
  };

  const handleSaveFixedClass = async () => {
    if (!editingFixedClass) return;

    try {
      await updateDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveClasses', editingFixedClass.id), {
        teacherName: editingFixedClass.teacherName,
        teacherEmail: editingFixedClass.teacherEmail,
        grade: editingFixedClass.grade,
        subject: editingFixedClass.subject || '',
        dayOfWeek: editingFixedClass.dayOfWeek,
        startTime: editingFixedClass.startTime,
        endTime: editingFixedClass.endTime,
        library: editingFixedClass.library,
        studentCount: editingFixedClass.studentCount,
      });
      await loadFixedClasses();
      setShowEditFixedClassModal(false);
      setEditingFixedClass(null);
      alert('Fixed class updated successfully!');
    } catch (err) {
      console.error('Error updating fixed class:', err);
      alert('Error updating fixed class: ' + err.message);
    }
  };

  const handleResendFixedClassEmail = async (cls) => {
    if (!settings?.emailEnabled) {
      const baseUrl = window.location.origin;
      const magicLink = `${baseUrl}/calendar/booking/${cls.magicLinkToken}`;
      alert(`Email notifications are disabled. Share this link with the teacher:\n\n${magicLink}`);
      return;
    }

    try {
      const baseUrl = window.location.origin;
      const magicLink = `${baseUrl}/calendar/booking/${cls.magicLinkToken}`;
      const scheduleText = cls.dayOfWeek === 'everyday'
        ? 'Every school day (Mon-Fri)'
        : `Every ${cls.dayOfWeek}`;

      await sendBookingEmail({
        type: 'fixed_class_scheduled',
        to: cls.teacherEmail,
        teacherName: cls.teacherName,
        bookingDate: scheduleText,
        bookingTime: `${formatTime12(cls.startTime)} - ${formatTime12(cls.endTime)}`,
        library: cls.library,
        grade: cls.grade,
        librarianName: settings?.yourName,
        schoolName: settings?.schoolName,
        magicLinkUrl: magicLink,
        scheduleText: scheduleText,
        replyTo: settings?.librarianEmail,
      });
      alert(`Email sent to ${cls.teacherEmail}!`);
    } catch (err) {
      console.error('Error resending email:', err);
      alert('Error sending email: ' + err.message);
    }
  };

  const handleAddAide = async () => {
    if (!newAide.name) {
      alert('Please enter the aide name');
      return;
    }
    if (!newAide.assignedBlocks || newAide.assignedBlocks.length === 0) {
      alert('Please assign at least one schedule block');
      return;
    }
    try {
      await addDoc(collection(db, 'users', currentUser.uid, 'nornsWeaveAides'), {
        name: newAide.name,
        email: newAide.email || '',
        dayType: newAide.dayType,
        assignedBlocks: newAide.assignedBlocks,
        createdAt: new Date().toISOString()
      });
      setNewAide({ name: '', email: '', dayType: 'A', assignedBlocks: [] });
      setShowAddAideModal(false);
      await loadAides();
    } catch (err) {
      console.error('Error adding aide:', err);
      alert('Error adding aide.');
    }
  };

  const handleDeleteAide = async (aideId) => {
    if (window.confirm('Delete this aide?')) {
      try {
        await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveAides', aideId));
        await loadAides();
      } catch (err) {
        console.error('Error deleting aide:', err);
      }
    }
  };

  const handleEditAide = (aide) => {
    setEditingAide({ ...aide });
    setShowEditAideModal(true);
  };

  const handleSaveAide = async () => {
    if (!editingAide?.name || !editingAide?.assignedBlocks?.length) {
      alert('Please fill in name and assign at least one block');
      return;
    }
    try {
      await updateDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveAides', editingAide.id), {
        name: editingAide.name,
        email: editingAide.email || '',
        dayType: editingAide.dayType,
        assignedBlocks: editingAide.assignedBlocks
      });
      setEditingAide(null);
      setShowEditAideModal(false);
      await loadAides();
      showNotification('Aide updated successfully', 'success');
    } catch (err) {
      console.error('Error updating aide:', err);
      alert('Error updating aide.');
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (window.confirm('Are you sure you want to delete this booking?')) {
      try {
        await deleteDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveBookings', bookingId));
        await loadBookings();
        alert('Booking deleted successfully.');
      } catch (err) {
        console.error('Error deleting booking:', err);
        alert('Error deleting booking.');
      }
    }
  };

  const handleUpdateBooking = async (e) => {
    e.preventDefault();
    try {
      const { id, ...updateData } = selectedBooking;
      await updateDoc(doc(db, 'users', currentUser.uid, 'nornsWeaveBookings', id), updateData);
      setShowEditModal(false);
      setSelectedBooking(null);
      await loadBookings();
      alert('Booking updated successfully!');
    } catch (err) {
      console.error('Error updating booking:', err);
      alert('Error updating booking.');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/gatekeeper');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // getWeekDates, getDayName come from context

  // Check if we can navigate to the previous week (stay within academic year)
  const canNavigatePrevious = () => {
    if (!yearSetupForm.academicYearStart) return true;
    const firstDayOfWeek = getWeekDates(weekOffset - 1)[0];
    return firstDayOfWeek >= yearSetupForm.academicYearStart;
  };

  // Check if we can navigate to the next week (stay within academic year)
  const canNavigateNext = () => {
    if (!yearSetupForm.academicYearEnd) return true;
    const lastDayOfWeek = getWeekDates(weekOffset + 1)[4];
    return lastDayOfWeek <= yearSetupForm.academicYearEnd;
  };

  // Check if a recurring item's day matches a specific date
  // Supports 'everyday' option which matches Mon-Fri
  const matchesDay = (itemDay, checkDayName) => {
    if (itemDay === 'everyday') {
      return ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].includes(checkDayName);
    }
    return itemDay === checkDayName;
  };

  const doTimesOverlap = (start1, end1, start2, end2) => {
    return (start1 < end2 && end1 > start2);
  };

  const getEventsForSlot = (library, date, startTime, endTime) => {
    const dayName = getDayName(date);
    const events = [];

    bookings.forEach(b => {
      if ((b.library === library || b.library === 'both') &&
          b.date === date &&
          doTimesOverlap(startTime, endTime, b.startTime, b.endTime)) {
        events.push({ ...b, eventType: 'booking' });
      }
    });

    // Add fixed classes
    fixedClasses.forEach(c => {
      if ((c.library === library || c.library === 'both') &&
          matchesDay(c.dayOfWeek, dayName) &&
          doTimesOverlap(startTime, endTime, c.startTime, c.endTime)) {
        // Check for exceptions
        const hasException = c.exceptions?.some(ex => ex.date === date);
        if (!hasException) {
          events.push({ ...c, eventType: 'fixedClass' });
        }
      }
    });

    settingsForm.librarianBlocks?.forEach(b => {
      if ((b.library === library || b.library === 'both') &&
          matchesDay(b.day, dayName) &&
          doTimesOverlap(startTime, endTime, b.startTime, b.endTime)) {
        events.push({ ...b, eventType: 'block' });
      }
    });

    personalEvents.forEach(e => {
      if ((e.library === library || e.library === 'both') &&
          e.date === date &&
          doTimesOverlap(startTime, endTime, e.startTime, e.endTime)) {
        events.push({ ...e, eventType: 'personal' });
      }
    });

    return events;
  };

  // Helper to check if a date is a mass day (regular mass day, holy day, adoration, or mass_day override)
  const isMassDay = (date) => {
    // Check for mass_day schedule override first (explicitly set)
    const massDayOverride = (settingsForm.scheduleOverrides || []).find(o => {
      const overrideStart = o.startDate;
      const overrideEnd = o.endDate || o.startDate;
      return date >= overrideStart && date <= overrideEnd && o.type === 'mass_day';
    });
    if (massDayOverride) {
      return true;
    }

    // Check if it's an adoration day (uses mass day schedule)
    const adorationOnDate = adorationDays.find(ad => ad.date === date);
    if (adorationOnDate) {
      return true;
    }

    const dayName = getDayName(date);
    const regularMassDay = settings?.massSchedule?.regularDay || 'Thursday';

    // Check if it's the regular mass day
    if (dayName === regularMassDay) {
      // But check if mass was shifted away due to a holy day this week
      const dateObj = new Date(date + 'T00:00:00');
      const weekStart = new Date(dateObj);
      weekStart.setDate(dateObj.getDate() - dateObj.getDay() + 1); // Monday
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 4); // Friday

      const holyDayShiftingMass = holyDays.find(hd => {
        const hdDate = new Date(hd.date + 'T00:00:00');
        const isInWeek = hdDate >= weekStart && hdDate <= weekEnd;
        return isInWeek && hd.shiftsMass;
      });

      // If a holy day shifted mass away, this is NOT a mass day
      return !holyDayShiftingMass;
    }

    // Check if it's a holy day that has mass
    const holyDayOnDate = holyDays.find(hd => hd.date === date);
    if (holyDayOnDate) {
      return true; // Holy days have mass
    }

    return false;
  };

  // Check if a date is a school day (not holiday, not workday, not closed)
  const isSchoolDay = (date) => {
    // Check for holidays
    const holiday = schoolCalendarEvents.find(e => {
      const eventStart = e.startDate;
      const eventEnd = e.endDate || e.startDate;
      return date >= eventStart && date <= eventEnd && e.type === 'holiday';
    });
    if (holiday) return false;

    // Check for workdays (teachers work, no students)
    const workday = schoolCalendarEvents.find(e => {
      const eventStart = e.startDate;
      const eventEnd = e.endDate || e.startDate;
      return date >= eventStart && date <= eventEnd && e.type === 'workday';
    });
    if (workday) return false;

    // Check for full-day closures
    const closure = closures.find(c => {
      const closureStart = c.startDate;
      const closureEnd = c.endDate || c.startDate;
      return date >= closureStart && date <= closureEnd && c.timeType === 'full';
    });
    if (closure) return false;

    return true;
  };

  // Get all aides for a slot (for both shading and tooltip display)
  const getAllAidesForSlot = (library, startTime, endTime, useMassDaySchedule) => {
    const aideScheduleBlocks = settingsForm.aideScheduleBlocks || [];
    const regularBlocks = aideScheduleBlocks.filter(b => !b.scheduleType || b.scheduleType === 'regular');
    const massDayBlocks = aideScheduleBlocks.filter(b => b.scheduleType === 'mass_day');

    return aides.filter(aide => {
      const assignedBlocks = aide.assignedBlocks || [];

      let blocksToCheck;
      if (useMassDaySchedule) {
        const aideHasMassDayBlocks = assignedBlocks.some(blockId =>
          massDayBlocks.some(b => b.id === blockId)
        );
        blocksToCheck = aideHasMassDayBlocks ? massDayBlocks : regularBlocks;
      } else {
        blocksToCheck = regularBlocks;
      }

      return assignedBlocks.some(blockId => {
        const block = blocksToCheck.find(b => b.id === blockId);
        if (!block) return false;
        const matchesLibrary = block.library === library || block.library === 'both';
        const matchesTime = doTimesOverlap(startTime, endTime, block.startTime, block.endTime);
        return matchesLibrary && matchesTime;
      });
    });
  };

  // Check if a specific time slot has aide coverage
  // Returns { hasCoverage: boolean, isMassDay: boolean, aideNames: string[] } for styling and tooltip
  const hasAideCoverage = (library, date, startTime, endTime) => {
    // No aide coverage on non-school days
    if (!isSchoolDay(date)) {
      return { hasCoverage: false, isMassDay: false, aideNames: [] };
    }

    const massDayFlag = isMassDay(date);

    // Get ALL aides for this slot regardless of A/B day
    const allAides = getAllAidesForSlot(library, startTime, endTime, massDayFlag);

    // Sort aides: A Day before B Day
    const sortedAides = [...allAides].sort((a, b) => {
      if (a.dayType === 'A' && b.dayType === 'B') return -1;
      if (a.dayType === 'B' && b.dayType === 'A') return 1;
      return a.name.localeCompare(b.name); // Alphabetical within same day type
    });

    return {
      hasCoverage: sortedAides.length > 0,
      isMassDay: massDayFlag,
      aideNames: sortedAides.map(a => `${a.name} (${a.dayType})`)
    };
  };

  const getEventColor = (event) => {
    const colors = settingsForm.colors;
    if (event.eventType === 'booking') {
      return event.needsLibrarian ? colors.needsLibrarian : colors.independent;
    }
    if (event.eventType === 'fixedClass') return colors.fixedClass;
    if (event.eventType === 'block') return colors.blocked;
    if (event.eventType === 'closure') return '#dc2626'; // Red for closures
    if (event.eventType === 'holyDay') return '#7c3aed'; // Purple with gold accent for holy days
    if (event.eventType === 'adoration') return '#8b5cf6'; // Purple for adoration
    if (event.eventType === 'holiday') return '#dc2626'; // Red for holidays
    if (event.eventType === 'halfDay') return '#f97316'; // Orange for half days (legacy)
    if (event.eventType === 'halfDayBanner') return '#f97316'; // Orange for half day banners
    if (event.eventType === 'libraryEvent') return '#eab308'; // Yellow for library events (book fair, etc)
    if (event.eventType === 'massBlock') return '#7c3aed'; // Purple for mass time blocks
    if (event.eventType === 'mass') return '#7c3aed'; // Purple for regular mass
    return colors.personal;
  };

  // Generate time slots from start to end time in 30-minute increments
  const generateTimeSlotsForRange = (startTime, endTime) => {
    const slots = [];
    if (!startTime || !endTime) return slots;

    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);

    let currentHour = startHour;
    let currentMin = startMin;

    while (currentHour < endHour || (currentHour === endHour && currentMin <= endMin)) {
      slots.push(`${currentHour.toString().padStart(2, '0')}:${currentMin.toString().padStart(2, '0')}`);
      currentMin += 30;
      if (currentMin >= 60) {
        currentMin = 0;
        currentHour++;
      }
    }

    return slots;
  };

  // Generate hour-only time slots for cleaner labels
  const generateHourSlotsForRange = (startTime, endTime) => {
    const slots = [];
    if (!startTime || !endTime) return slots;

    const [startHour] = startTime.split(':').map(Number);
    const [endHour] = endTime.split(':').map(Number);

    for (let hour = startHour; hour <= endHour; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
    }

    return slots;
  };

  // Get time slots for a specific library from settings
  const getLibraryTimeSlots = (library) => {
    if (!settings) {
      // Fallback to default hours
      return generateTimeSlotsForRange('07:30', '16:00');
    }

    if (library === 'elementary') {
      const start = settings.elementaryHours?.start || settingsForm.elementaryStart || '07:30';
      const end = settings.elementaryHours?.end || settingsForm.elementaryEnd || '16:00';
      return generateTimeSlotsForRange(start, end);
    } else {
      const start = settings.middleSchoolHours?.start || settingsForm.middleSchoolStart || '08:00';
      const end = settings.middleSchoolHours?.end || settingsForm.middleSchoolEnd || '15:30';
      return generateTimeSlotsForRange(start, end);
    }
  };

  // Get library hours for a specific date (handles Saturday hours)
  const getLibraryHoursForDate = (library, date) => {
    // Check if the date is a Saturday
    if (date && isSaturdayOpen(date)) {
      return getSaturdayHours();
    }
    return getLibraryHours(library);
  };

  // Get hour-only time slots for a specific library (for clean grid labels)
  const getLibraryHourSlots = (library, date = null) => {
    const hours = date ? getLibraryHoursForDate(library, date) : getLibraryHours(library);
    return generateHourSlotsForRange(hours.start, hours.end);
  };

  // Legacy function for backwards compatibility
  const generateTimeSlots = () => {
    // Use the larger range between both libraries for shared views
    const elemSlots = getLibraryTimeSlots('elementary');
    const middleSlots = getLibraryTimeSlots('middle');

    // Combine and dedupe, maintaining order
    const allSlots = [...new Set([...elemSlots, ...middleSlots])];
    allSlots.sort((a, b) => a.localeCompare(b));
    return allSlots;
  };

  // Calculate position percentages for absolute positioning of events
  // IMPORTANT: The grid displays whole hours (07:00, 08:00, etc.) so we need to
  // calculate positions relative to the floor of the start hour, not the exact start time
  const getEventPosition = (eventStart, eventEnd, dayStart, dayEnd) => {
    // Get the floor hour for grid alignment (e.g., 07:30 -> 07:00, 16:00 -> 16:00)
    const [startHour] = dayStart.split(':').map(Number);
    const [endHour] = dayEnd.split(':').map(Number);
    const gridStartMinutes = startHour * 60; // Floor to whole hour (07:30 -> 07:00)
    const gridEndMinutes = endHour * 60; // Use the end hour directly (16:00 -> 16:00)
    const totalGridMinutes = gridEndMinutes - gridStartMinutes;

    const eventStartMinutes = timeToMinutes(eventStart);
    const eventEndMinutes = timeToMinutes(eventEnd);

    // Clamp to grid bounds
    const clampedStart = Math.max(eventStartMinutes, gridStartMinutes);
    const clampedEnd = Math.min(eventEndMinutes, gridEndMinutes);

    const topPercent = ((clampedStart - gridStartMinutes) / totalGridMinutes) * 100;
    const heightPercent = ((clampedEnd - clampedStart) / totalGridMinutes) * 100;

    // Ensure minimum height for readability (at least 2%)
    const minHeight = 2;
    const finalHeight = Math.max(heightPercent, minHeight);

    return { top: topPercent, height: finalHeight };
  };

  // Get all events for a specific day and library (for absolute positioning)
  const getAllEventsForDay = (library, date) => {
    const dayName = getDayName(date);
    const events = [];

    // Check for schedule overrides that affect classes/bookings
    const scheduleOverride = (settingsForm.scheduleOverrides || []).find(o => {
      const overrideStart = o.startDate;
      const overrideEnd = o.endDate || o.startDate;
      const isInDateRange = date >= overrideStart && date <= overrideEnd;
      // Check if it applies to this library
      const appliesToLibrary = !o.library || o.library === 'both' || o.library === library;
      return isInDateRange && (o.type === 'no_bookings' || o.type === 'no_classes') && appliesToLibrary;
    });

    // Both no_classes and no_bookings block class-type bookings - only admin type allowed
    const isNoClasses = scheduleOverride?.type === 'no_classes' || scheduleOverride?.type === 'no_bookings';

    // Check for inventory period - blocks all classes, only admin bookings allowed
    const hasInventory = schoolCalendarEvents.some(calEvent => {
      const eventStart = calEvent.startDate;
      const eventEnd = calEvent.endDate || calEvent.startDate;
      const isInDateRange = date >= eventStart && date <= eventEnd;
      return isInDateRange && calEvent.type === 'inventory';
    });

    // Combined check: either schedule override or inventory blocks classes
    const isClassesBlocked = isNoClasses || hasInventory;

    // Add bookings - filter out class bookings if no_classes/no_bookings override or inventory is active
    bookings.forEach(b => {
      if ((b.library === library || b.library === 'both') && b.date === date) {
        // If no_classes, no_bookings override, or inventory is active, only show admin bookings
        if (isClassesBlocked && b.type !== 'admin') {
          return; // Skip non-admin bookings
        }
        events.push({ ...b, eventType: 'booking' });
      }
    });

    // Add fixed classes - but check for full-day closures, workdays, and schedule overrides first
    // Check if this date has a full-day closure (skip all fixed classes)
    const hasFullDayClosure = closures.some(closure => {
      const appliesToLibrary = closure.library === 'both' || closure.library === library;
      if (!appliesToLibrary) return false;
      const closureStart = closure.startDate;
      const closureEnd = closure.endDate || closure.startDate;
      const isInDateRange = date >= closureStart && date <= closureEnd;
      return isInDateRange && closure.timeType === 'full';
    });

    // Check if this date has a holiday or closed library event (skip all fixed classes)
    // NOTE: Exclude workdays - they are NOT closures even if libraryStatus is 'closed'
    const hasHolidayOrClosedEvent = schoolCalendarEvents.some(calEvent => {
      const eventStart = calEvent.startDate;
      const eventEnd = calEvent.endDate || calEvent.startDate;
      const isInDateRange = date >= eventStart && date <= eventEnd;
      // Workdays are handled separately - don't treat them as closures
      if (calEvent.type === 'workday') return false;
      return isInDateRange && (calEvent.type === 'holiday' || calEvent.libraryStatus === 'closed');
    });

    // Check if this date is a workday (teachers working, no students - hide fixed classes)
    const hasWorkday = schoolCalendarEvents.some(calEvent => {
      const eventStart = calEvent.startDate;
      const eventEnd = calEvent.endDate || calEvent.startDate;
      const isInDateRange = date >= eventStart && date <= eventEnd;
      return isInDateRange && calEvent.type === 'workday';
    });

    // Check if date is outside school days (within academic year but before/after school)
    const { schoolStartDate, schoolEndDate, academicYearStart, academicYearEnd } = yearSetupForm;
    const isOutsideSchoolDays = schoolStartDate && schoolEndDate && academicYearStart && academicYearEnd &&
      date >= academicYearStart && date <= academicYearEnd &&
      (date < schoolStartDate || date > schoolEndDate);

    // Check if this is a Saturday (no fixed classes on Saturdays, only bookings/events)
    const isSaturday = dayName === 'Saturday';

    // Only add fixed classes if there's no full-day closure, holiday, workday, schedule override, inventory, outside school days, or Saturday
    if (!hasFullDayClosure && !hasHolidayOrClosedEvent && !hasWorkday && !isOutsideSchoolDays && !isClassesBlocked && !isSaturday) {
      fixedClasses.forEach(c => {
        if ((c.library === library || c.library === 'both') && matchesDay(c.dayOfWeek, dayName)) {
          const hasException = c.exceptions?.some(ex => ex.date === date);
          if (!hasException) {
            events.push({ ...c, eventType: 'fixedClass' });
          }
        }
      });
    }

    // Add librarian blocks - use settings (from DB) not settingsForm (local state)
    const blocks = settings?.librarianBlocks || settingsForm.librarianBlocks || [];
    blocks.forEach(b => {
      if ((b.library === library || b.library === 'both') && matchesDay(b.day, dayName)) {
        events.push({ ...b, eventType: 'block' });
      }
    });

    // Add personal events (library events) - handle date ranges and all-day events
    const hours = getLibraryHours(library);
    personalEvents.forEach(e => {
      const appliesToLibrary = e.library === library || e.library === 'both';
      if (!appliesToLibrary) return;

      // Check if this date is within the event's date range
      const eventStart = e.date;
      const eventEnd = e.endDate || e.date;
      const isInDateRange = date >= eventStart && date <= eventEnd;
      if (!isInDateRange) return;

      // For all-day events, use library hours
      if (e.timeType === 'allDay') {
        events.push({
          ...e,
          startTime: hours.start,
          endTime: hours.end,
          eventType: 'personal',
          isAllDay: true
        });
      } else {
        events.push({ ...e, eventType: 'personal', isAllDay: false });
      }
    });

    // Add closures
    closures.forEach(closure => {
      // Check if this closure applies to this library
      const appliesToLibrary = closure.library === 'both' || closure.library === library;
      if (!appliesToLibrary) return;

      // Check if this date falls within the closure date range
      const closureStart = closure.startDate;
      const closureEnd = closure.endDate || closure.startDate;
      const isInDateRange = date >= closureStart && date <= closureEnd;
      if (!isInDateRange) return;

      // Get the display label
      const reasonLabel = CLOSURE_REASONS.find(r => r.value === closure.reason)?.label || closure.customReason || 'Closed';

      events.push({
        ...closure,
        startTime: closure.timeType === 'full' ? getLibraryHours(library).start : closure.startTime,
        endTime: closure.timeType === 'full' ? getLibraryHours(library).end : closure.endTime,
        eventType: 'closure',
        displayLabel: `🚫 ${reasonLabel}`
      });
    });

    // Add holy days - show mass time block on the holy day
    holyDays.forEach(holyDay => {
      if (holyDay.date === date) {
        const massStart = yearSetupForm.massStartTime || '09:00';
        const massEnd = yearSetupForm.massEndTime || '10:00';
        events.push({
          ...holyDay,
          startTime: massStart,
          endTime: massEnd,
          eventType: 'holyDay',
          displayLabel: `⛪ ${holyDay.name}`
        });
      }
    });

    // Check if this is the regular mass day (Thursday by default)
    // Show regular mass block, OR "Available" if mass shifted due to holy day
    const regularMassDay = settings?.massSchedule?.regularDay || 'Thursday';
    if (dayName === regularMassDay) {
      // Find holy days that shift mass (not on regular mass day, not on weekend)
      // Look for holy days in the same week as this date
      const dateObj = new Date(date + 'T00:00:00');
      const weekStart = new Date(dateObj);
      weekStart.setDate(dateObj.getDate() - dateObj.getDay() + 1); // Monday
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 4); // Friday

      const holyDayShiftingMass = holyDays.find(hd => {
        const hdDate = new Date(hd.date + 'T00:00:00');
        const isInWeek = hdDate >= weekStart && hdDate <= weekEnd;
        return isInWeek && hd.shiftsMass;
      });

      const massStart = yearSetupForm.massStartTime || '09:00';
      const massEnd = yearSetupForm.massEndTime || '10:00';

      if (holyDayShiftingMass) {
        // Mass moved to holy day - slot is simply open for bookings (no indicator needed)
      } else {
        // Show regular mass block on this day
        events.push({
          id: `regular-mass-${date}`,
          startTime: massStart,
          endTime: massEnd,
          eventType: 'mass',
          displayLabel: '⛪ Mass'
        });
      }
    }

    // Add adoration days - Friday adoration
    adorationDays.forEach(adorationDay => {
      if (adorationDay.date === date) {
        const massStart = yearSetupForm.massStartTime || '09:00';
        const massEnd = yearSetupForm.massEndTime || '10:00';
        events.push({
          ...adorationDay,
          startTime: massStart,
          endTime: massEnd,
          eventType: 'adoration',
          displayLabel: '🙏 Adoration'
        });
      }
    });

    // Add school calendar events (holidays, half days, library events)
    // NOTE: Workdays are displayed as banners (via getFullDayClosureInfo), not as blocking events
    schoolCalendarEvents.forEach(calEvent => {
      const eventStart = calEvent.startDate;
      const eventEnd = calEvent.endDate || calEvent.startDate;
      const isInDateRange = date >= eventStart && date <= eventEnd;
      if (!isInDateRange) return;

      // SKIP WORKDAYS - they are displayed as banners, not blocking events
      // Workdays should allow bookings, just hide fixed classes
      if (calEvent.type === 'workday') return;

      // Filter by library (holidays apply to both, but library events may be specific)
      // Holidays and closures always apply to both libraries
      const isUniversalEvent = calEvent.type === 'holiday' || calEvent.libraryStatus === 'closed';
      if (!isUniversalEvent) {
        const eventLibrary = calEvent.library || 'both';
        if (eventLibrary !== 'both' && eventLibrary !== library) return;
      }

      const hours = getLibraryHours(library);

      if (calEvent.type === 'holiday' || calEvent.libraryStatus === 'closed') {
        // Full day closure for holidays (not workdays - they're skipped above)
        events.push({
          ...calEvent,
          startTime: hours.start,
          endTime: hours.end,
          eventType: 'holiday',
          displayLabel: `🏖️ ${calEvent.name}`
        });
      } else if (calEvent.type === 'half-day') {
        // Half day - show as blocking event from 12:00 to end of day (like a closure)
        const isAfternoon = calEvent.halfDayPeriod === 'afternoon';
        if (isAfternoon) {
          // Afternoon dismissal - block from 12:00 to end of library hours
          events.push({
            ...calEvent,
            startTime: '12:00',
            endTime: hours.end,
            eventType: 'halfDay',
            displayLabel: `⏰ ${calEvent.name} - Early Dismissal`
          });
        } else {
          // Morning only - block from start to 12:00
          events.push({
            ...calEvent,
            startTime: hours.start,
            endTime: '12:00',
            eventType: 'halfDay',
            displayLabel: `⏰ ${calEvent.name} - AM Only`
          });
        }
      } else {
        // Library events (book fair, TLA, inventory, etc)
        events.push({
          ...calEvent,
          startTime: hours.start,
          endTime: hours.end,
          eventType: 'libraryEvent',
          displayLabel: calEvent.type === 'book-fair' ? `📚 ${calEvent.name}`
            : calEvent.type === 'tla' ? `📋 ${calEvent.name}`
            : calEvent.type === 'inventory' ? `📦 ${calEvent.name}`
            : `📌 ${calEvent.name}`
        });
      }
    });

    // Add mass time blocks (ad-hoc mass slot blocks)
    massTimeBlocks.forEach(massBlock => {
      if (massBlock.date === date) {
        const massStart = massBlock.useMassTime
          ? (yearSetupForm.massStartTime || '09:00')
          : massBlock.customStartTime;
        const massEnd = massBlock.useMassTime
          ? (yearSetupForm.massEndTime || '10:00')
          : massBlock.customEndTime;
        events.push({
          ...massBlock,
          startTime: massStart,
          endTime: massEnd,
          eventType: 'massBlock',
          displayLabel: `⛪ ${massBlock.reason || 'Mass Time Blocked'}`
        });
      }
    });

    // Debug: log if there are multiple blocks
    const blockCount = events.filter(e => e.eventType === 'block').length;
    if (blockCount > 1) {
      console.log('DEBUG: Multiple blocks for', library, date, events.filter(e => e.eventType === 'block'));
    }

    return events;
  };

  // Check if a day has a full-day closure (holiday, closure with timeType='full', or closed library event)
  const getFullDayClosureInfo = (library, date) => {
    // Check for full-day closures (from Closures section)
    const fullDayClosure = closures.find(closure => {
      const appliesToLibrary = closure.library === 'both' || closure.library === library;
      if (!appliesToLibrary) return false;
      const closureStart = closure.startDate;
      const closureEnd = closure.endDate || closure.startDate;
      const isInDateRange = date >= closureStart && date <= closureEnd;
      return isInDateRange && closure.timeType === 'full';
    });

    if (fullDayClosure) {
      const reasonLabel = CLOSURE_REASONS.find(r => r.value === fullDayClosure.reason)?.label || fullDayClosure.customReason || 'Closed';
      return { isClosed: true, label: `🚫 ${reasonLabel}`, type: 'closure', data: fullDayClosure };
    }

    // Check for holidays and closed library events (from Year Setup)
    // NOTE: Exclude workdays - they should show as gray banner, not red closure
    const closedCalEvent = schoolCalendarEvents.find(calEvent => {
      const eventStart = calEvent.startDate;
      const eventEnd = calEvent.endDate || calEvent.startDate;
      const isInDateRange = date >= eventStart && date <= eventEnd;
      if (!isInDateRange) return false;

      // IMPORTANT: Workdays are NOT closures even if they have libraryStatus === 'closed'
      // Workdays should show as gray banner, allow bookings, just hide fixed classes
      if (calEvent.type === 'workday') return false;

      const isHolidayOrClosed = calEvent.type === 'holiday' || calEvent.libraryStatus === 'closed';
      if (!isHolidayOrClosed) return false;

      // Holidays apply to all libraries, but library-specific closures should be filtered
      const eventLibrary = calEvent.library || 'both';
      if (calEvent.type !== 'holiday' && eventLibrary !== 'both' && eventLibrary !== library) {
        return false;
      }

      return true;
    });

    if (closedCalEvent) {
      const label = closedCalEvent.type === 'holiday'
        ? `🏖️ ${closedCalEvent.name}`
        : `🚫 ${closedCalEvent.name}`;
      return { isClosed: true, label, type: closedCalEvent.type, data: closedCalEvent };
    }

    // Check for workdays (teachers working, no students - bookings allowed, classes hidden)
    const workdayEvent = schoolCalendarEvents.find(calEvent => {
      const eventStart = calEvent.startDate;
      const eventEnd = calEvent.endDate || calEvent.startDate;
      const isInDateRange = date >= eventStart && date <= eventEnd;
      return isInDateRange && calEvent.type === 'workday';
    });

    if (workdayEvent) {
      return { isClosed: false, isWorkday: true, label: `📋 ${workdayEvent.name}`, type: 'workday', data: workdayEvent };
    }

    // Check for "outside school days" - within academic year but before/after school days
    // These are treated like workdays (no fixed classes, but bookings allowed)
    const { schoolStartDate, schoolEndDate, academicYearStart, academicYearEnd } = yearSetupForm;
    if (schoolStartDate && schoolEndDate && academicYearStart && academicYearEnd) {
      const isWithinAcademicYear = date >= academicYearStart && date <= academicYearEnd;
      const isOutsideSchoolDays = date < schoolStartDate || date > schoolEndDate;

      if (isWithinAcademicYear && isOutsideSchoolDays) {
        return { isClosed: false, isWorkday: true, label: '📋 Teacher Workday', type: 'outsideSchool' };
      }
    }

    // Check for schedule override (no_classes or no_bookings type)
    const scheduleOverride = (settingsForm.scheduleOverrides || []).find(o => {
      const overrideStart = o.startDate;
      const overrideEnd = o.endDate || o.startDate;
      const isInDateRange = date >= overrideStart && date <= overrideEnd;
      const appliesToLibrary = !o.library || o.library === 'both' || o.library === library;
      return isInDateRange && (o.type === 'no_bookings' || o.type === 'no_classes') && appliesToLibrary;
    });

    if (scheduleOverride) {
      const isNoBookings = scheduleOverride.type === 'no_bookings';
      return {
        isClosed: false,
        isWorkday: false,
        isNoBookings: isNoBookings,
        isNoClasses: !isNoBookings,
        label: isNoBookings
          ? `🚫 ${scheduleOverride.description || 'No Classes/Bookings'}`
          : `📋 ${scheduleOverride.description || 'No Classes'}`,
        type: isNoBookings ? 'noBookings' : 'noClasses',
        data: scheduleOverride
      };
    }

    // Check for inventory period (blocks all classes, only admin bookings allowed)
    const inventoryEvent = schoolCalendarEvents.find(calEvent => {
      const eventStart = calEvent.startDate;
      const eventEnd = calEvent.endDate || calEvent.startDate;
      const isInDateRange = date >= eventStart && date <= eventEnd;
      return isInDateRange && calEvent.type === 'inventory';
    });

    if (inventoryEvent) {
      return {
        isClosed: false,
        isWorkday: false,
        isInventory: true,
        label: `📦 Library Inventory`,
        type: 'inventory',
        data: inventoryEvent
      };
    }

    // Check for all-day library events (from Library Events section)
    const allDayLibraryEvent = personalEvents.find(e => {
      const appliesToLibrary = e.library === 'both' || e.library === library;
      if (!appliesToLibrary) return false;
      const eventStart = e.date;
      const eventEnd = e.endDate || e.date;
      const isInDateRange = date >= eventStart && date <= eventEnd;
      return isInDateRange && e.timeType === 'allDay';
    });

    if (allDayLibraryEvent) {
      return {
        isClosed: false,
        isWorkday: false,
        isLibraryEvent: true,
        label: `📖 ${allDayLibraryEvent.title}`,
        type: 'libraryEvent',
        data: allDayLibraryEvent
      };
    }

    return { isClosed: false, isWorkday: false };
  };

  // getLibraryHours comes from context

  // Computed values - weekDates, thisWeek, thisWeekBookings come from context
  const timeSlots = generateTimeSlots();

  const getWeekLabel = () => {
    if (weekOffset === 0) return 'This Week';
    if (weekOffset === 1) return 'Next Week';
    if (weekOffset === -1) return 'Last Week';
    const startDate = new Date(weekDates[0]);
    return startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading Norns' Weave...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50">
      {/* Toast Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 px-6 py-4 rounded-lg shadow-lg z-50 transition-all ${
          notification.type === 'success'
            ? 'bg-green-500 text-white'
            : 'bg-red-500 text-white'
        }`}>
          <div className="flex items-center gap-2">
            {notification.type === 'success' ? '✓' : '✗'}
            <span className="font-medium">{notification.message}</span>
          </div>
        </div>
      )}
      <header className="bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-600 text-white shadow-xl print:hidden">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Logo size={40} showText={false} />
              <button onClick={() => navigate('/asgard')} className="text-lg sm:text-2xl font-bold hover:text-purple-200 tracking-wider">
                HEIMDL
              </button>
              <span className="hidden sm:inline text-purple-300">|</span>
              <span className="hidden sm:inline text-purple-200">The Norns' Weave - Admin</span>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-6">
              <div className="text-right">
                <p className="text-sm text-purple-300">This Week</p>
                <p className="text-xl font-bold">{thisWeekBookings.length} bookings</p>
              </div>
              <button
                onClick={handleLogout}
                className="text-sm sm:text-base bg-purple-700 hover:bg-purple-600 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg transition-colors"
              >
                Leave Asgard
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-6 bg-white rounded-xl p-2 shadow-lg print:hidden flex-wrap">
          <button onClick={() => setView('pending')}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all relative ${
              view === 'pending' ? 'bg-orange-600 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}>
            🔔 Pending
            {pendingBookings.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                {pendingBookings.length}
              </span>
            )}
          </button>
          <button onClick={() => setView('calendar')}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
              view === 'calendar' ? 'bg-purple-600 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}>
            📅 Calendar
          </button>
          <button onClick={() => setView('list')}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
              view === 'list' ? 'bg-purple-600 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}>
            📋 List
          </button>
          <button onClick={() => setView('fixed')}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
              view === 'fixed' ? 'bg-purple-600 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}>
            📌 Fixed Classes
          </button>
          <button onClick={() => setView('aides')}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
              view === 'aides' ? 'bg-purple-600 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}>
            🙋 Aides
          </button>
          <button onClick={() => setView('yearSetup')}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
              view === 'yearSetup' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}>
            📅 Year Setup
          </button>
          <button onClick={() => setView('stats')}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
              view === 'stats' ? 'bg-purple-600 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}>
            📊 Stats
          </button>
          <button onClick={() => setView('settings')}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
              view === 'settings' ? 'bg-purple-600 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}>
            ⚙️ Settings
          </button>
          <button onClick={() => setView('print')}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
              view === 'print' ? 'bg-purple-600 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}>
            🖨️ Print
          </button>
        </div>

        {/* Pending Requests View */}
        {view === 'pending' && (
          <PendingTab
            onApproveBooking={handleApproveBooking}
            onDeclineBooking={openDeclineModal}
            onCreateFixedClass={(booking, getDayNameFn) => {
              setNewFixedClass({
                teacherName: booking.teacherName || '',
                teacherEmail: booking.teacherEmail || '',
                grade: booking.grade || '',
                subject: booking.purpose || '',
                dayOfWeek: booking.recurringDay || getDayNameFn(booking.date),
                startTime: booking.startTime || '',
                duration: '',
                endTime: booking.endTime || '',
                library: booking.library || 'elementary',
                studentCount: booking.studentCount || ''
              });
              setShowAddFixedClassModal(true);
            }}
            getDayName={getDayName}
          />
        )}

        {/* Calendar View */}
        {view === 'calendar' && (
          <CalendarTab
            canNavigatePrevious={canNavigatePrevious}
            canNavigateNext={canNavigateNext}
            getWeekLabel={getWeekLabel}
            getLibraryHours={getLibraryHours}
            getLibraryHoursForDate={getLibraryHoursForDate}
            getLibraryHourSlots={getLibraryHourSlots}
            getSaturdayHours={getSaturdayHours}
            getAllEventsForDay={getAllEventsForDay}
            getFullDayClosureInfo={getFullDayClosureInfo}
            getEventPosition={getEventPosition}
            getEventColor={getEventColor}
            hasAideCoverage={hasAideCoverage}
            onAddEvent={() => setShowAddEventModal(true)}
            onAddOverride={() => setShowAddOverrideModal(true)}
            onAddSingleClass={() => setShowAddSingleClassModal(true)}
            onAddClosure={() => {
              setClosureForm({
                reason: '',
                customReason: '',
                dateType: 'single',
                startDate: new Date().toISOString().split('T')[0],
                endDate: '',
                timeType: 'full',
                startTime: '',
                endTime: '',
                library: 'both',
                notifyOption: 'immediate',
                customMessage: ''
              });
              setAffectedBookings([]);
              setShowClosureModal(true);
            }}
            onEditBooking={(booking) => { setSelectedBooking(booking); setShowEditModal(true); }}
            onDeletePersonalEvent={handleDeletePersonalEvent}
            onDeleteClosure={handleDeleteClosure}
            onDeleteSchoolCalendarEvent={handleDeleteSchoolCalendarEvent}
          />
        )}

        {/* List View */}
        {view === 'list' && (
          <ListTab
            onEditBooking={(booking) => { setSelectedBooking(booking); setShowEditModal(true); }}
            onDeleteBooking={handleDeleteBooking}
            onDeletePersonalEvent={handleDeletePersonalEvent}
          />
        )}

        {/* Fixed Classes View */}
        {view === 'fixed' && (
          <FixedClassesTab
            onAddFixedClass={() => setShowAddFixedClassModal(true)}
            onEditFixedClass={handleEditFixedClass}
            onDeleteFixedClass={handleDeleteFixedClass}
            onResendFixedClassEmail={handleResendFixedClassEmail}
          />
        )}

        {/* Aides View */}
        {view === 'aides' && (
          <AidesTab
            onAddAide={() => setShowAddAideModal(true)}
            onEditAide={handleEditAide}
            onDeleteAide={handleDeleteAide}
            onSaveSettings={autoSaveSettings}
          />
        )}

        {/* Statistics View */}
        {view === 'stats' && <StatsTab />}

        {/* Year Setup View */}
        {view === 'yearSetup' && (
          <YearSetupTab
            onToggleLock={toggleYearSetupLock}
            onStartWizard={() => {
              setWizardHolyDays([]);
              setWizardAdorationDays([]);
              setWizardHolidays([]);
              setWizardHalfDays([]);
              setWizardWorkdays([]);
              setWizardLibraryEvents([]);
              // Initialize Saturday config with existing settings if available
              setWizardSaturdayConfig({
                enabled: saturdaySchedule?.enabled || false,
                startTime: saturdaySchedule?.startTime || '09:00',
                endTime: saturdaySchedule?.endTime || '13:00',
                dates: saturdaySchedule?.dates || []
              });
              setYearSetupStep(1);
              setShowYearSetupWizard(true);
            }}
            onFixWorkdays={fixWorkdayStatuses}
            onCleanupDuplicates={cleanupAllDuplicates}
            onClearAllData={clearAllCalendarData}
            onAddHolyDay={handleAddHolyDay}
            onDeleteHolyDay={handleDeleteHolyDay}
            onEditHolyDay={handleEditHolyDay}
            onAddAdorationDay={handleAddAdorationDay}
            onDeleteAdorationDay={handleDeleteAdorationDay}
            onEditAdorationDay={handleEditAdorationDay}
            onAddSchoolCalendarEvent={handleAddSchoolCalendarEvent}
            onDeleteSchoolCalendarEvent={handleDeleteSchoolCalendarEvent}
            onEditSchoolCalendarEvent={handleEditSchoolCalendarEvent}
            onAddSaturdayOpening={handleAddSaturdayOpening}
            onDeleteSaturdayOpening={handleDeleteSaturdayOpening}
            onEditSaturdayOpening={handleEditSaturdayOpening}
            onAddMassTimeBlock={handleAddMassTimeBlock}
            onDeleteMassTimeBlock={handleDeleteMassTimeBlock}
            savingHolyDay={savingHolyDay}
            savingAdorationDay={savingAdorationDay}
            savingSchoolCalendarEvent={savingSchoolCalendarEvent}
            savingMassBlock={savingMassBlock}
          />
        )}

        {/* Print View */}
        {view === 'print' && (
          <PrintTab
            timeSlots={timeSlots}
            getEventsForSlot={getEventsForSlot}
            getWeekLabel={getWeekLabel}
          />
        )}

        {/* Settings View */}
        {view === 'settings' && (
          <SettingsTab
            onSettingsSave={handleSettingsSave}
            onCredentialsSave={handleCredentialsSave}
            onAddBlock={() => setShowAddBlockModal(true)}
            onRemoveBlock={handleRemoveLibrarianBlock}
            onAddTeacher={handleAddTeacher}
            onDeleteTeacher={handleDeleteTeacher}
            autoSaveSettings={autoSaveSettings}
            showNotification={showNotification}
          />
        )}
      </main>

      {/* Modals */}

      {/* Decline Modal */}
      {showDeclineModal && declineBooking && (
        <DeclineModal
          booking={declineBooking}
          onClose={() => { setShowDeclineModal(false); setDeclineBooking(null); }}
          onDecline={(booking, reason, alternatives) => {
            setDeclineReason(reason);
            setDeclineAlternatives(alternatives);
            handleDeclineBooking();
          }}
        />
      )}

      {/* Add Schedule Block Modal */}
      {showAddBlockModal && (
        <AddBlockModal
          block={newBlock}
          setBlock={setNewBlock}
          onClose={() => setShowAddBlockModal(false)}
          onAdd={handleAddLibrarianBlock}
        />
      )}

      {/* Quick Library Closure Modal */}
      {showClosureModal && (
        <ClosureModal
          closureForm={closureForm}
          setClosureForm={setClosureForm}
          affectedBookings={affectedBookings}
          updateAffectedBookings={updateAffectedBookings}
          onClose={() => setShowClosureModal(false)}
          onSave={handleSaveClosure}
        />
      )}

      {/* Add Library Event Modal */}
      {showAddEventModal && (
        <AddEventModal
          event={newEvent}
          setEvent={setNewEvent}
          onClose={() => setShowAddEventModal(false)}
          onSubmit={handleAddPersonalEvent}
        />
      )}

      {/* Add Fixed Class Modal */}
      {showAddFixedClassModal && (
        <AddFixedClassModal
          fixedClass={newFixedClass}
          setFixedClass={setNewFixedClass}
          teachers={teachers}
          onClose={() => setShowAddFixedClassModal(false)}
          onAdd={handleAddFixedClass}
        />
      )}

      {/* Add Single Class Modal */}
      {showAddSingleClassModal && (
        <AddSingleClassModal
          singleClass={newSingleClass}
          setSingleClass={setNewSingleClass}
          teachers={teachers}
          onClose={() => setShowAddSingleClassModal(false)}
          onAdd={handleAddSingleClass}
        />
      )}

      {/* Edit Fixed Class Modal */}
      {showEditFixedClassModal && editingFixedClass && (
        <EditFixedClassModal
          fixedClass={editingFixedClass}
          setFixedClass={setEditingFixedClass}
          onClose={() => { setShowEditFixedClassModal(false); setEditingFixedClass(null); }}
          onSave={handleSaveFixedClass}
        />
      )}

      {/* Add Aide Modal */}
      {showAddAideModal && (
        <AddAideModal
          aide={newAide}
          setAide={setNewAide}
          aideScheduleBlocks={settingsForm.aideScheduleBlocks || []}
          onClose={() => { setShowAddAideModal(false); setNewAide({ name: '', email: '', dayType: 'A', assignedBlocks: [] }); }}
          onAdd={handleAddAide}
        />
      )}

      {/* Edit Aide Modal */}
      {showEditAideModal && editingAide && (
        <EditAideModal
          aide={editingAide}
          setAide={setEditingAide}
          aideScheduleBlocks={settingsForm.aideScheduleBlocks || []}
          onClose={() => { setShowEditAideModal(false); setEditingAide(null); }}
          onSave={handleSaveAide}
        />
      )}

      {/* Edit Booking Modal */}
      {showEditModal && selectedBooking && (
        <EditBookingModal
          booking={selectedBooking}
          setBooking={setSelectedBooking}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleUpdateBooking}
        />
      )}

      {/* Add Academic Calendar Event Modal */}
      {showAddCalendarEventModal && (
        <AddCalendarEventModal
          event={newCalendarEvent}
          setEvent={setNewCalendarEvent}
          onClose={() => setShowAddCalendarEventModal(false)}
          onAdd={() => {}}
          showNotification={showNotification}
          settingsForm={settingsForm}
          setSettingsForm={setSettingsForm}
        />
      )}

      {/* Add Schedule Override Modal */}
      {showAddOverrideModal && (
        <AddOverrideModal
          override={newOverride}
          setOverride={setNewOverride}
          onClose={() => setShowAddOverrideModal(false)}
          onAdd={() => {}}
          showNotification={showNotification}
          settingsForm={settingsForm}
          setSettingsForm={setSettingsForm}
          autoSaveSettings={autoSaveSettings}
          fixedClasses={fixedClasses}
          settings={settings}
        />
      )}

      {/* Year Setup Wizard Modal */}
      {showYearSetupWizard && (
        <YearSetupWizard
          yearSetupForm={yearSetupForm}
          setYearSetupForm={setYearSetupForm}
          yearSetupStep={yearSetupStep}
          setYearSetupStep={setYearSetupStep}
          wizardHolyDays={wizardHolyDays}
          setWizardHolyDays={setWizardHolyDays}
          wizardAdorationDays={wizardAdorationDays}
          setWizardAdorationDays={setWizardAdorationDays}
          wizardHolidays={wizardHolidays}
          setWizardHolidays={setWizardHolidays}
          wizardHalfDays={wizardHalfDays}
          setWizardHalfDays={setWizardHalfDays}
          wizardWorkdays={wizardWorkdays}
          setWizardWorkdays={setWizardWorkdays}
          wizardLibraryEvents={wizardLibraryEvents}
          setWizardLibraryEvents={setWizardLibraryEvents}
          wizardSaturdayConfig={wizardSaturdayConfig}
          setWizardSaturdayConfig={setWizardSaturdayConfig}
          getDayOfWeekFromDate={getDayOfWeekFromDate}
          showNotification={showNotification}
          onClose={() => setShowYearSetupWizard(false)}
          onSave={handleSaveYearSetup}
          hasPreviousYearData={hasPreviousYearData}
          previousYearLabel={previousYearLabel}
          onCopyFromPreviousYear={copyFromPreviousYear}
          isCopyingYear={isCopyingYear}
        />
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:hidden {
            display: none !important;
          }
          #root > div > main > div:last-child,
          #root > div > main > div:last-child * {
            visibility: visible;
          }
          #root > div > main > div:last-child {
            position: absolute;
            left: 0;
            top: 0;
          }
        }
      `}</style>
    </div>
  );
}

// Wrap the component with the provider
function AdminNornsWeaveWithProvider() {
  return (
    <AdminNornsWeaveProvider>
      <AdminNornsWeave />
    </AdminNornsWeaveProvider>
  );
}

export default AdminNornsWeaveWithProvider;
